import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Package, Upload, Link2, Search, Filter, Trash2,
  Edit3, Eye, ExternalLink, AlertTriangle, CheckCircle, Clock,
  Plus, FileSpreadsheet, RefreshCw, X, ChevronRight, Tag, Percent,
  MessageSquare, Loader2, Star, Table, HardDrive, FileText, Palette, Image, Camera,
  Sun, Moon, Check, Archive, Sparkles, Wand2, Download, Mic, FolderOpen, Tags
} from 'lucide-react';
import { AdminSidebar, AdminSidebarToggle } from '../components/admin/AdminSidebar';
import { AIProductImageGenerator } from '../components/admin/AIProductImageGenerator';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { cn } from '../lib/utils';
import { VoiceInput } from '../components/ui/VoiceInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return '/placeholder.png';
  
  // Si c'est un objet, extraire l'URL
  if (typeof imagePath === 'object') {
    imagePath = imagePath.url || imagePath.src || imagePath[0] || null;
    if (!imagePath) return '/placeholder.png';
    // Récursif au cas où c'est encore un objet
    if (typeof imagePath === 'object') {
      imagePath = imagePath.url || imagePath.src || null;
      if (!imagePath) return '/placeholder.png';
    }
  }
  
  // Vérifier que c'est une string
  if (typeof imagePath !== 'string') return '/placeholder.png';
  
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/api/')) return `${API_URL}${imagePath}`;
  if (imagePath.startsWith('/uploads/')) return `${API_URL}/api${imagePath}`;
  return `${API_URL}/api/uploads/${imagePath}`;
};

// Component for Margin Rules by Category
function MarginRulesSection() {
  const [categories, setCategories] = useState([]);
  const [marginRules, setMarginRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories/admin/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || []);
          // Initialize margin rules from existing data or defaults
          const rules = {};
          (data.categories || []).forEach(cat => {
            rules[cat.id] = cat.margin_multiplier || 2.5;
          });
          setMarginRules(rules);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, [token]);

  const handleMarginChange = (categoryId, value) => {
    setMarginRules(prev => ({
      ...prev,
      [categoryId]: parseFloat(value) || 2.5
    }));
  };

  const handleSaveMargins = async () => {
    try {
      setSaving(true);
      // Save margin rules to each category
      for (const [catId, margin] of Object.entries(marginRules)) {
        await fetch(`${API_URL}/api/categories/admin/${catId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ margin_multiplier: margin })
        });
      }
      toast.success('Règles de marge enregistrées');
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400 mb-4">Aucune catégorie disponible</p>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/admin/categories'}
          className="border-indigo-500/50 text-indigo-400"
        >
          Créer des catégories
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3">
        {categories.map(cat => (
          <div 
            key={cat.id}
            className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/50 border border-zinc-700"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{cat.emoji || '🏷️'}</span>
              <div>
                <p className="text-white font-medium">{cat.name?.fr || cat.slug}</p>
                <p className="text-xs text-gray-500">{cat.products_count || 0} produits</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-sm">×</span>
              <Input
                type="number"
                step="0.1"
                min="1"
                max="10"
                value={marginRules[cat.id] || 2.5}
                onChange={(e) => handleMarginChange(cat.id, e.target.value)}
                className="w-20 bg-zinc-900 border-zinc-700 text-white text-center"
              />
              <span className="text-gray-400 text-sm">=</span>
              <span className="text-green-400 font-mono text-sm w-16">
                {((marginRules[cat.id] || 2.5) * 100).toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="flex justify-end pt-4 border-t border-zinc-800">
        <Button 
          onClick={handleSaveMargins}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          Enregistrer les marges
        </Button>
      </div>
    </div>
  );
}

export function AdminSourcingPage() {
  const navigate = useNavigate();
  const { isLight, toggleTheme } = useAdminTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [activeTab, setActiveTab] = useState('catalog');
  
  // Import state - Multi-URL avec journal de progression
  const [importUrl, setImportUrl] = useState('');
  const [importUrls, setImportUrls] = useState([]); // Liste des URLs à importer
  const [importOptions, setImportOptions] = useState({
    generate_ai_name: true,
    generate_ai_description: true,
    as_draft: true,
    category_id: null,
    margin_rule: 2.5,
    tags: []
  });
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({
    current: 0,
    total: 0,
    currentUrl: '',
    logs: [],
    status: 'idle' // idle, running, completed, error
  });
  
  // Catalog state
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    needs_enrichment: null,
    imported_period: ''  // today, yesterday, 7d, 14d, 30d
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // 50 ou 100
  const totalPages = Math.ceil(totalProducts / pageSize);
  const [showAiImageGenerator, setShowAiImageGenerator] = useState(false);
  
  // Categories for bulk assignment
  const [categories, setCategories] = useState([]);
  
  // Selected product (side panel)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  
  // Reviews import state
  const [isImportingReviews, setIsImportingReviews] = useState(false);
  const [reviewsImportCount, setReviewsImportCount] = useState(15);
  
  // Google Sheets import state
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState('');
  const [isImportingSheets, setIsImportingSheets] = useState(false);
  
  // Multi-URL import modal state
  const [showMultiUrlModal, setShowMultiUrlModal] = useState(false);
  const [multiUrlText, setMultiUrlText] = useState('');
  const [detectedUrls, setDetectedUrls] = useState([]);
  
  // Manual product creation state
  const [manualProduct, setManualProduct] = useState({});
  
  // Atmospheres state
  const [atmospheres, setAtmospheres] = useState([]);
  const [selectedAtmosphereId, setSelectedAtmosphereId] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  
  // Bulk categorization state
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [isAssigningCategories, setIsAssigningCategories] = useState(false);
  
  // Free prompt generation state
  const [freePromptMode, setFreePromptMode] = useState(false);
  const [freePrompt, setFreePrompt] = useState('');
  const [freePromptStyle, setFreePromptStyle] = useState('editorial');
  
  // Category scraping state
  const [categoryUrl, setCategoryUrl] = useState('');
  const [isScrapingCategory, setIsScrapingCategory] = useState(false);
  const [categoryScrapeJob, setCategoryScrapeJob] = useState(null);
  const [categoryPreview, setCategoryPreview] = useState(null); // Preview before import
  const [categoryImportCount, setCategoryImportCount] = useState(50); // Number of products to import
  
  // Image cleanup state
  const [isCleaningImages, setIsCleaningImages] = useState(false);

  // Sort order state
  const [sortOrder, setSortOrder] = useState('desc'); // desc = plus récent d'abord
  const [sortField, setSortField] = useState('date'); // 'date', 'margin', 'price', 'status'

  // LitBuy re-enrichment state
  const [litbuyReenrichJob, setLitbuyReenrichJob] = useState(null);
  const [isStartingReenrich, setIsStartingReenrich] = useState(false);

  // Bulk rescrape state
  const [bulkRescrapeJob, setBulkRescrapeJob] = useState(null);
  const [lastScrapeInfo, setLastScrapeInfo] = useState(null);
  
  // Batch rescrape broken images state
  const [batchRescrapeStatus, setBatchRescrapeStatus] = useState(null);
  
  // Telegram bulk import state
  const [telegramImportJob, setTelegramImportJob] = useState(null);

  // Bulk margin state
  const [showMarginDialog, setShowMarginDialog] = useState(false);
  const [selectedMargin, setSelectedMargin] = useState(3.0);
  const [isApplyingMargin, setIsApplyingMargin] = useState(false);

  // Hidden status notifications state
  const [hiddenStatuses, setHiddenStatuses] = useState({
    batchRescrape: false,
    telegramImport: false
  });
  
  // Select all products from database state
  const [selectAllFromDB, setSelectAllFromDB] = useState(false);

  // Check authentication
  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      fetchData(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchData = async (authToken) => {
    try {
      // Fetch products
      await fetchProducts(authToken);
      
      // Fetch categories (admin API pour avoir les IDs)
      const catRes = await fetch(`${API_URL}/api/categories/admin/all`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (catRes.ok) {
        const data = await catRes.json();
        setCategories(data.categories || []);
      }
      
      // Fetch atmospheres
      const atmRes = await fetch(`${API_URL}/api/admin/atmospheres`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (atmRes.ok) {
        const atmData = await atmRes.json();
        setAtmospheres(atmData.atmospheres || []);
      }
      
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/sourcing/stats`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      // Fetch last scrape info
      const scrapeRes = await fetch(`${API_URL}/api/telegram/scrape/status`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (scrapeRes.ok) {
        const scrapeData = await scrapeRes.json();
        setLastScrapeInfo(scrapeData);
      }
      
      // Fetch batch rescrape status
      const batchRes = await fetch(`${API_URL}/api/sourcing/batch/rescrape-status`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (batchRes.ok) {
        const batchData = await batchRes.json();
        setBatchRescrapeStatus(batchData);
      }
      
      // Fetch telegram import job status
      const importRes = await fetch(`${API_URL}/api/telegram/import-job-latest`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (importRes.ok) {
        const importData = await importRes.json();
        setTelegramImportJob(importData);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Refetch products when filters change
  useEffect(() => {
    if (token) {
      fetchProducts(token);
    }
  }, [filters.status, filters.search, filters.needs_enrichment, filters.imported_period]);

  // Poll batch rescrape status when running
  useEffect(() => {
    if (batchRescrapeStatus?.status === 'running' && token) {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`${API_URL}/api/sourcing/batch/rescrape-status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setBatchRescrapeStatus(data);
            if (data.status === 'completed') {
              fetchProducts();
            }
          }
        } catch (err) {
          console.error('Error polling batch status:', err);
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [batchRescrapeStatus?.status, token]);

  // Poll telegram import job status when running
  useEffect(() => {
    if (telegramImportJob?.status === 'running' && token) {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`${API_URL}/api/telegram/import-job-latest`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setTelegramImportJob(data);
            if (data.status === 'completed') {
              fetchProducts();
            }
          }
        } catch (err) {
          console.error('Error polling import status:', err);
        }
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [telegramImportJob?.status, token]);

  const fetchProducts = async (authToken = token, page = currentPage, limit = pageSize, sortBy = sortField, order = sortOrder) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.needs_enrichment !== null) params.append('needs_enrichment', filters.needs_enrichment);
      if (filters.imported_period) params.append('imported_period', filters.imported_period);
      params.append('limit', String(limit));
      params.append('skip', String((page - 1) * limit));
      
      // Add sorting parameters
      const sortFieldMap = {
        'date': 'created_at',
        'price': 'source_price.price_usd',
        'selling_price': 'pricing.prices.USD',
        'margin': 'margin_percent',
        'status': 'status',
        'sizes': 'variants_count',
        'colors': 'colors_count'
      };
      params.append('sort_by', sortFieldMap[sortBy] || 'created_at');
      params.append('sort_order', order);
      
      const res = await fetch(`${API_URL}/api/sourcing/products?${params}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        setTotalProducts(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };
  
  // Changer de page - conserver la sélection
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Réinitialiser selectAllFromDB si on change de page (car la sélection devient partielle)
    // On garde la sélection des produits mais on remet le flag à false
    setSelectAllFromDB(false);
    fetchProducts(token, newPage, pageSize);
  };
  
  // Changer la taille de page
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setSelectAllFromDB(false);
    // Ne pas réinitialiser la sélection
    fetchProducts(token, 1, newSize);
  };

  // Import handlers - Multi-URL avec journal de progression
  
  // Ajouter une URL à la liste
  const addUrlToList = () => {
    if (!importUrl.trim()) {
      toast.error('Entrez une URL valide');
      return;
    }
    
    // Vérifier si l'URL est valide (ACBUY)
    if (!importUrl.includes('acbuy.com')) {
      toast.error('Seules les URLs ACBUY sont supportées');
      return;
    }
    
    // Vérifier les doublons dans la liste
    if (importUrls.some(u => u.url === importUrl.trim())) {
      toast.error('Cette URL est déjà dans la liste');
      return;
    }
    
    setImportUrls(prev => [...prev, { 
      url: importUrl.trim(), 
      id: Date.now(),
      status: 'pending' // pending, importing, success, error
    }]);
    setImportUrl('');
    toast.success('URL ajoutée à la liste');
  };
  
  // Supprimer une URL de la liste
  const removeUrlFromList = (urlId) => {
    setImportUrls(prev => prev.filter(u => u.id !== urlId));
  };
  
  // Vider la liste
  const clearUrlList = () => {
    setImportUrls([]);
    setImportProgress({ current: 0, total: 0, currentUrl: '', logs: [], status: 'idle' });
  };
  
  // Ajouter un log de progression
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('fr-FR');
    setImportProgress(prev => ({
      ...prev,
      logs: [...prev.logs, { timestamp, message, type }]
    }));
  };
  
  // Fonction pour détecter et extraire les URLs du texte collé
  const handleMultiUrlPaste = (text) => {
    setMultiUrlText(text);
    
    // Regex pour détecter les URLs (ACBUY et autres)
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
    const foundUrls = text.match(urlRegex) || [];
    
    // Filtrer pour ne garder que les URLs valides et uniques
    const validUrls = [...new Set(foundUrls)]
      .filter(url => url.includes('acbuy.com') || url.includes('1688.com') || url.includes('taobao.com'))
      .map((url, index) => ({
        url: url.trim(),
        id: Date.now() + index,
        status: 'pending'
      }));
    
    setDetectedUrls(validUrls);
    
    if (validUrls.length > 0) {
      toast.success(`${validUrls.length} URL(s) détectée(s)`);
    }
  };
  
  // Confirmer et ajouter les URLs détectées à la liste d'import
  const confirmMultiUrlImport = () => {
    if (detectedUrls.length === 0) {
      toast.error('Aucune URL valide détectée');
      return;
    }
    
    // Filtrer les doublons avec les URLs déjà dans la liste
    const existingUrls = new Set(importUrls.map(u => u.url));
    const newUrls = detectedUrls.filter(u => !existingUrls.has(u.url));
    
    if (newUrls.length === 0) {
      toast.info('Toutes les URLs sont déjà dans la liste');
    } else {
      setImportUrls(prev => [...prev, ...newUrls]);
      toast.success(`${newUrls.length} URL(s) ajoutée(s) à la liste`);
    }
    
    // Fermer le modal et réinitialiser
    setShowMultiUrlModal(false);
    setMultiUrlText('');
    setDetectedUrls([]);
  };
  
  // Importer toutes les URLs de la liste
  const handleImportAllUrls = async () => {
    if (importUrls.length === 0) {
      toast.error('Ajoutez au moins une URL à importer');
      return;
    }
    
    setIsImporting(true);
    setImportProgress({
      current: 0,
      total: importUrls.length,
      currentUrl: '',
      logs: [],
      status: 'running'
    });
    
    addLog(`🚀 Démarrage de l'import de ${importUrls.length} produit(s)`, 'info');
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < importUrls.length; i++) {
      const urlItem = importUrls[i];
      
      // Mettre à jour le statut de l'URL en cours
      setImportUrls(prev => prev.map(u => 
        u.id === urlItem.id ? { ...u, status: 'importing' } : u
      ));
      
      setImportProgress(prev => ({
        ...prev,
        current: i + 1,
        currentUrl: urlItem.url
      }));
      
      addLog(`📦 [${i + 1}/${importUrls.length}] Import: ${urlItem.url.substring(0, 50)}...`, 'info');
      addLog(`⏳ Connexion au site ACBUY...`, 'step');
      
      try {
        // Étape 1: Démarrer le job (asynchrone)
        const response = await fetch(`${API_URL}/api/sourcing/import/url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            url: urlItem.url,
            options: importOptions
          })
        });
        
        const data = await response.json();
        
        if (response.status === 409) {
          // Doublon
          addLog(`⚠️ Doublon détecté: ${data.detail?.existing_product_name || 'Produit existant'}`, 'warning');
          setImportUrls(prev => prev.map(u => 
            u.id === urlItem.id ? { ...u, status: 'error', error: 'Doublon' } : u
          ));
          errorCount++;
        } else if (!response.ok) {
          // Erreur
          addLog(`❌ Erreur: ${data.detail?.message || data.detail || 'Erreur inconnue'}`, 'error');
          setImportUrls(prev => prev.map(u => 
            u.id === urlItem.id ? { ...u, status: 'error', error: data.detail?.message } : u
          ));
          errorCount++;
        } else {
          // Job démarré - maintenant on fait le polling pour attendre la vraie fin
          const jobId = data.job_id;
          addLog(`📡 Job créé: ${jobId} - Scraping en cours...`, 'step');
          
          // Polling du statut du job
          let jobCompleted = false;
          let pollAttempts = 0;
          const maxPollAttempts = 90; // 3 minutes max (90 × 2s)
          
          while (!jobCompleted && pollAttempts < maxPollAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // Attendre 2s
            pollAttempts++;
            
            try {
              const statusResponse = await fetch(`${API_URL}/api/sourcing/import/job/${jobId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              const job = await statusResponse.json();
              
              // Logs de progression
              if (pollAttempts === 3) addLog(`🔍 Extraction des données...`, 'step');
              if (pollAttempts === 10) addLog(`📸 Téléchargement des images...`, 'step');
              if (pollAttempts === 20 && (importOptions.generate_ai_name || importOptions.generate_ai_description)) {
                addLog(`🤖 Génération IA du contenu...`, 'step');
              }
              
              if (job.status === 'completed') {
                jobCompleted = true;
                addLog(`✅ Scraping terminé - données extraites`, 'step');
                addLog(`💾 Sauvegarde: "${job.result?.product?.name?.fr || 'Produit importé'}"`, 'success');
                
                setImportUrls(prev => prev.map(u => 
                  u.id === urlItem.id ? { ...u, status: 'success', productName: job.result?.product?.name?.fr } : u
                ));
                successCount++;
              } else if (job.status === 'failed') {
                jobCompleted = true;
                addLog(`❌ Erreur: ${job.result?.message || job.result?.error || 'Échec'}`, 'error');
                setImportUrls(prev => prev.map(u => 
                  u.id === urlItem.id ? { ...u, status: 'error', error: job.result?.error } : u
                ));
                errorCount++;
              } else if (job.status === 'duplicate') {
                jobCompleted = true;
                addLog(`⚠️ Doublon: ${job.result?.existing_product_name || 'Produit existant'}`, 'warning');
                setImportUrls(prev => prev.map(u => 
                  u.id === urlItem.id ? { ...u, status: 'error', error: 'Doublon' } : u
                ));
                errorCount++;
              }
              // Si running, on continue le polling
              
            } catch (pollErr) {
              console.error('Polling error:', pollErr);
            }
          }
          
          // Timeout après 3 minutes
          if (!jobCompleted) {
            addLog(`⏱️ Timeout - Le produit sera peut-être importé plus tard`, 'warning');
            setImportUrls(prev => prev.map(u => 
              u.id === urlItem.id ? { ...u, status: 'error', error: 'Timeout' } : u
            ));
            errorCount++;
          }
        }
      } catch (err) {
        addLog(`❌ Erreur de connexion: ${err.message}`, 'error');
        setImportUrls(prev => prev.map(u => 
          u.id === urlItem.id ? { ...u, status: 'error', error: 'Erreur réseau' } : u
        ));
        errorCount++;
      }
      
      // Petite pause entre les imports pour éviter de surcharger
      if (i < importUrls.length - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
    
    // Fin de l'import
    addLog(`🏁 Import terminé: ${successCount} réussi(s), ${errorCount} erreur(s)`, 
      errorCount > 0 ? 'warning' : 'success');
    
    setImportProgress(prev => ({
      ...prev,
      status: errorCount === importUrls.length ? 'error' : 'completed'
    }));
    
    setIsImporting(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} produit(s) importé(s) avec succès`);
      fetchProducts();
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} erreur(s) durant l'import`);
    }
  };
  
  // Import d'une seule URL (mode rapide)
  const handleImportUrl = async () => {
    if (!importUrl.trim()) {
      toast.error('Entrez une URL valide');
      return;
    }
    
    // Ajouter à la liste et importer
    const tempId = Date.now();
    setImportUrls([{ url: importUrl.trim(), id: tempId, status: 'pending' }]);
    setImportUrl('');
    
    // Lancer l'import
    setIsImporting(true);
    setImportProgress({
      current: 0,
      total: 1,
      currentUrl: importUrl.trim(),
      logs: [],
      status: 'running'
    });
    
    addLog(`🚀 Import rapide démarré`, 'info');
    addLog(`⏳ Connexion au site ACBUY...`, 'step');
    
    try {
      // Étape 1: Démarrer le job
      const startResponse = await fetch(`${API_URL}/api/sourcing/import/url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: importUrl.trim(),
          options: importOptions
        })
      });
      
      const startData = await startResponse.json();
      
      if (!startResponse.ok) {
        addLog(`❌ Erreur: ${startData.detail || 'Erreur au démarrage'}`, 'error');
        setImportUrls([{ url: importUrl.trim(), id: tempId, status: 'error' }]);
        setImportProgress(prev => ({ ...prev, status: 'error' }));
        setIsImporting(false);
        return;
      }
      
      const jobId = startData.job_id;
      addLog(`📡 Job créé: ${jobId}`, 'step');
      addLog(`⏳ Scraping en cours...`, 'info');
      
      // Étape 2: Polling du statut
      let attempts = 0;
      const maxAttempts = 60; // 2 minutes max (60 × 2s)
      
      const pollStatus = async () => {
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000)); // 2 secondes
          attempts++;
          
          try {
            const statusResponse = await fetch(`${API_URL}/api/sourcing/import/job/${jobId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const job = await statusResponse.json();
            
            if (job.status === 'running') {
              if (attempts === 5) addLog(`🔍 Extraction des données...`, 'step');
              if (attempts === 15) addLog(`📸 Téléchargement des images...`, 'step');
              if (attempts === 25) addLog(`🤖 Génération IA en cours...`, 'step');
              continue;
            }
            
            if (job.status === 'completed') {
              addLog(`✅ Import terminé avec succès!`, 'success');
              const productName = job.result?.product?.name?.fr || 'Produit importé';
              addLog(`💾 Produit créé: "${productName}"`, 'success');
              setImportUrls([{ url: importUrl.trim(), id: tempId, status: 'success', productName }]);
              setImportProgress(prev => ({ ...prev, status: 'completed', current: 1 }));
              fetchProducts();
              return;
            }
            
            if (job.status === 'duplicate') {
              addLog(`⚠️ Doublon: ${job.result?.existing_product_name || 'Ce produit existe déjà'}`, 'warning');
              setImportUrls([{ url: importUrl.trim(), id: tempId, status: 'error', error: 'Doublon' }]);
              setImportProgress(prev => ({ ...prev, status: 'error' }));
              return;
            }
            
            if (job.status === 'failed') {
              addLog(`❌ Erreur: ${job.result?.message || job.result?.error || 'Échec de l\'import'}`, 'error');
              setImportUrls([{ url: importUrl.trim(), id: tempId, status: 'error' }]);
              setImportProgress(prev => ({ ...prev, status: 'error' }));
              return;
            }
            
          } catch (pollErr) {
            console.error('Polling error:', pollErr);
          }
        }
        
        // Timeout après 2 minutes de polling
        addLog(`⏱️ Timeout - Vérifiez le Catalogue`, 'warning');
        setImportUrls([{ url: importUrl.trim(), id: tempId, status: 'error' }]);
        setImportProgress(prev => ({ ...prev, status: 'error' }));
      };
      
      await pollStatus();
      
    } catch (err) {
      addLog(`❌ Erreur de connexion: ${err.message}`, 'error');
      setImportUrls([{ url: importUrl.trim(), id: tempId, status: 'error' }]);
      setImportProgress(prev => ({ ...prev, status: 'error' }));
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('as_draft', importOptions.as_draft);
    formData.append('margin_rule', importOptions.margin_rule);
    if (importOptions.category_id) {
      formData.append('category_id', importOptions.category_id);
    }
    
    setIsImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/sourcing/import/csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Import démarré! Job ID: ${data.job_id}`);
        fetchProducts();
      } else {
        toast.error(data.detail || 'Erreur d\'import');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setIsImporting(false);
      e.target.value = '';
    }
  };

  // Category scraping - Step 1: Preview (analyze without importing)
  const handleAnalyzeCategory = async () => {
    if (!categoryUrl.trim()) {
      toast.error('Entrez une URL de catégorie valide');
      return;
    }
    
    if (!categoryUrl.includes('acbuy.gg')) {
      toast.error('Seules les URLs acbuy.gg sont supportées');
      return;
    }
    
    setIsScrapingCategory(true);
    setCategoryPreview(null);
    setCategoryScrapeJob({ status: 'analyzing', products_found: 0 });
    
    try {
      // Call the preview endpoint (preview_only=true)
      const response = await fetch(`${API_URL}/api/sourcing/scrape-category/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: categoryUrl
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setCategoryScrapeJob(null);
        toast.error(data.detail || 'Erreur lors de l\'analyse');
        setIsScrapingCategory(false);
        return;
      }
      
      // Show preview with product count
      setCategoryPreview({
        url: categoryUrl,
        category: data.category,
        total_products: data.total_products,
        products_sample: data.products_sample || []
      });
      setCategoryImportCount(Math.min(data.total_products, 50)); // Default to 50 or total
      setCategoryScrapeJob(null);
      toast.success(`${data.total_products} produits trouvés dans "${data.category || 'toutes catégories'}"`);
      
    } catch (err) {
      setCategoryScrapeJob(null);
      toast.error('Erreur de connexion');
    } finally {
      setIsScrapingCategory(false);
    }
  };

  // Category scraping - Step 2: Confirm and start import
  const handleConfirmCategoryImport = async () => {
    if (!categoryPreview) return;
    
    setIsScrapingCategory(true);
    setCategoryScrapeJob({ status: 'importing', products_found: categoryImportCount });
    
    try {
      const response = await fetch(`${API_URL}/api/sourcing/scrape-category`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: categoryPreview.url,
          options: importOptions,
          max_products: categoryImportCount
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setCategoryScrapeJob({ status: 'failed', result: { error: data.detail || 'Erreur' } });
        toast.error(data.detail || 'Erreur lors de l\'import');
        setIsScrapingCategory(false);
        return;
      }
      
      toast.success(`Import de ${categoryImportCount} produits lancé!`);
      setCategoryScrapeJob({
        status: 'importing',
        products_found: data.products_found,
        job_id: data.job_id,
        import_progress: { current: 0, total: data.products_found }
      });
      setCategoryPreview(null); // Hide preview
      
      // Polling pour suivre la progression
      const jobId = data.job_id;
      let attempts = 0;
      const maxAttempts = 300; // 10 minutes max
      
      const pollJobStatus = async () => {
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
          
          try {
            const statusResponse = await fetch(`${API_URL}/api/sourcing/import/job/${jobId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const job = await statusResponse.json();
            
            if (job.status === 'importing' || job.status === 'extracted') {
              setCategoryScrapeJob(prev => ({
                ...prev,
                status: 'importing',
                import_progress: job.import_progress || prev.import_progress
              }));
              continue;
            }
            
            if (job.status === 'completed') {
              setCategoryScrapeJob({
                status: 'completed',
                products_found: data.products_found,
                result: job.result
              });
              toast.success(`Import terminé! ${job.result?.imported || 0} produits importés`);
              fetchProducts();
              setCategoryUrl('');
              break;
            }
            
            if (job.status === 'failed') {
              setCategoryScrapeJob({
                status: 'failed',
                result: job.result
              });
              toast.error('Erreur lors de l\'import');
              break;
            }
            
          } catch (pollErr) {
            console.error('Polling error:', pollErr);
          }
        }
      };
      
      await pollJobStatus();
      
    } catch (err) {
      setCategoryScrapeJob({ status: 'failed', result: { error: err.message } });
      toast.error('Erreur de connexion');
    } finally {
      setIsScrapingCategory(false);
    }
  };

  // Cancel category preview
  const handleCancelCategoryPreview = () => {
    setCategoryPreview(null);
    setCategoryScrapeJob(null);
  };

  // Cleanup images - remove invalid references
  const handleCleanupImages = async () => {
    if (isCleaningImages) return;
    
    setIsCleaningImages(true);
    try {
      const response = await fetch(`${API_URL}/api/products/admin/cleanup-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchProducts(); // Refresh product list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors du nettoyage');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setIsCleaningImages(false);
    }
  };

  // LitBuy Re-enrichment - Start job
  const handleStartLitbuyReenrich = async () => {
    if (isStartingReenrich || litbuyReenrichJob?.status === 'running') return;
    
    setIsStartingReenrich(true);
    try {
      const response = await fetch(`${API_URL}/api/sourcing/re-enrich-litbuy?limit=100`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`Ré-enrichissement de ${data.count} produits LitBuy lancé`);
        setLitbuyReenrichJob({
          job_id: data.job_id,
          status: 'running',
          total: data.count,
          processed: 0,
          enriched: 0,
          failed: 0
        });
        // Start polling for status
        pollLitbuyReenrichStatus(data.job_id);
      } else {
        toast.error(data.message || 'Erreur lors du lancement');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setIsStartingReenrich(false);
    }
  };

  // LitBuy Re-enrichment - Poll status
  const pollLitbuyReenrichStatus = async (jobId) => {
    let retries = 0;
    const maxRetries = 600; // 10 minutes max (1 poll per second)
    
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sourcing/re-enrich-litbuy/status/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          retries++;
          if (retries < maxRetries) {
            setTimeout(checkStatus, 2000);
          }
          return;
        }
        
        const data = await response.json();
        
        setLitbuyReenrichJob({
          job_id: jobId,
          status: data.status,
          total: data.total,
          processed: data.processed,
          enriched: data.enriched,
          failed: data.failed,
          logs: data.logs
        });
        
        if (data.status === 'running') {
          setTimeout(checkStatus, 3000); // Poll every 3 seconds
        } else if (data.status === 'completed') {
          toast.success(`Ré-enrichissement terminé: ${data.enriched} produits enrichis`);
          fetchProducts(); // Refresh product list
        }
      } catch (err) {
        retries++;
        if (retries < maxRetries) {
          setTimeout(checkStatus, 5000);
        }
      }
    };
    
    checkStatus();
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (selectedProducts.length === 0) {
      toast.error('Sélectionnez des produits');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/sourcing/products/bulk-action?action=${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedProducts)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedProducts([]);
        fetchProducts();
      } else {
        toast.error('Erreur lors de l\'action');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  // Bulk rescrape products
  const handleBulkRescrape = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Sélectionnez des produits');
      return;
    }
    
    setBulkRescrapeJob({
      status: 'running',
      total: selectedProducts.length,
      completed: 0,
      current: '',
      results: []
    });
    
    const results = [];
    for (let i = 0; i < selectedProducts.length; i++) {
      const productId = selectedProducts[i];
      const product = products.find(p => p._id === productId);
      
      setBulkRescrapeJob(prev => ({
        ...prev,
        completed: i,
        current: product?.name?.fr || productId
      }));
      
      try {
        const response = await fetch(`${API_URL}/api/sourcing/products/${productId}/rescrape`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const data = await response.json();
        results.push({
          id: productId,
          name: product?.name?.fr,
          success: response.ok,
          message: data.message || data.detail
        });
      } catch (err) {
        results.push({
          id: productId,
          name: product?.name?.fr,
          success: false,
          message: err.message
        });
      }
    }
    
    setBulkRescrapeJob({
      status: 'completed',
      total: selectedProducts.length,
      completed: selectedProducts.length,
      results
    });
    
    const successCount = results.filter(r => r.success).length;
    toast.success(`${successCount}/${selectedProducts.length} produits rescrapés`);
    setSelectedProducts([]);
    fetchProducts();
  };

  // Bulk apply margin
  const handleBulkApplyMargin = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Sélectionnez des produits');
      return;
    }
    
    setIsApplyingMargin(true);
    try {
      const response = await fetch(`${API_URL}/api/sourcing/products/bulk-apply-margin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: selectedProducts,
          margin_multiplier: selectedMargin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.updated} produit(s) mis à jour avec marge ${data.margin_applied}`);
        setShowMarginDialog(false);
        setSelectedProducts([]);
        fetchProducts();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setIsApplyingMargin(false);
    }
  };

  // Bulk assign categories
  const handleBulkAssignCategories = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Sélectionnez des produits');
      return;
    }
    
    if (selectedCategoryIds.length === 0) {
      toast.error('Sélectionnez au moins une catégorie');
      return;
    }
    
    setIsAssigningCategories(true);
    try {
      const response = await fetch(`${API_URL}/api/sourcing/products/bulk-assign-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: selectedProducts,
          category_ids: selectedCategoryIds
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.updated_count} produit(s) mis à jour`);
        setShowCategoryDialog(false);
        setSelectedCategoryIds([]);
        setSelectedProducts([]);
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'assignation');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setIsAssigningCategories(false);
    }
  };

  // Product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Sort by date - API call for full database sorting
  const handleSortByDate = () => {
    const newOrder = (sortField === 'date' && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortField('date');
    setSortOrder(newOrder);
    setCurrentPage(1); // Reset to page 1 when sorting
    fetchProducts(token, 1, pageSize, 'date', newOrder);
  };

  // Sort by margin - API call for full database sorting
  const handleSortByMargin = () => {
    const newOrder = (sortField === 'margin' && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortField('margin');
    setSortOrder(newOrder);
    setCurrentPage(1);
    fetchProducts(token, 1, pageSize, 'margin', newOrder);
  };

  // Sort by purchase price - API call for full database sorting
  const handleSortByPrice = () => {
    const newOrder = (sortField === 'price' && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortField('price');
    setSortOrder(newOrder);
    setCurrentPage(1);
    fetchProducts(token, 1, pageSize, 'price', newOrder);
  };

  // Sort by selling price - API call for full database sorting
  const handleSortBySellingPrice = () => {
    const newOrder = (sortField === 'selling_price' && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortField('selling_price');
    setSortOrder(newOrder);
    setCurrentPage(1);
    fetchProducts(token, 1, pageSize, 'selling_price', newOrder);
  };

  // Sort by status - API call for full database sorting
  const handleSortByStatus = () => {
    const newOrder = (sortField === 'status' && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortField('status');
    setSortOrder(newOrder);
    setCurrentPage(1);
    fetchProducts(token, 1, pageSize, 'status', newOrder);
  };

  // Sort by sizes count - API call for full database sorting
  const handleSortBySizes = () => {
    const newOrder = (sortField === 'sizes' && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortField('sizes');
    setSortOrder(newOrder);
    setCurrentPage(1);
    fetchProducts(token, 1, pageSize, 'sizes', newOrder);
  };

  // Sort by colors count - API call for full database sorting
  const handleSortByColors = () => {
    const newOrder = (sortField === 'colors' && sortOrder === 'desc') ? 'asc' : 'desc';
    setSortField('colors');
    setSortOrder(newOrder);
    setCurrentPage(1);
    fetchProducts(token, 1, pageSize, 'colors', newOrder);
  };

  // Sélectionner/désélectionner tous les produits de la page actuelle
  const selectAllProducts = () => {
    const currentPageIds = products.map(p => p._id);
    const allCurrentSelected = currentPageIds.every(id => selectedProducts.includes(id));
    
    if (allCurrentSelected) {
      // Désélectionner uniquement les produits de la page actuelle
      setSelectedProducts(prev => prev.filter(id => !currentPageIds.includes(id)));
    } else {
      // Ajouter les produits de la page actuelle à la sélection (sans dupliquer)
      setSelectedProducts(prev => {
        const newSelection = [...prev];
        currentPageIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };
  
  // Vérifier si tous les produits de la page actuelle sont sélectionnés
  const areAllCurrentPageSelected = () => {
    if (products.length === 0) return false;
    return products.every(p => selectedProducts.includes(p._id));
  };

  // Sélectionner TOUS les produits de la base de données
  const selectAllProductsFromDB = async () => {
    try {
      // Récupérer tous les IDs de produits
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.needs_enrichment !== null) params.append('needs_enrichment', filters.needs_enrichment);
      if (filters.imported_period) params.append('imported_period', filters.imported_period);
      params.append('limit', '1000'); // Limite haute pour récupérer tous les produits
      params.append('skip', '0');
      
      const res = await fetch(`${API_URL}/api/sourcing/products?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const allIds = (data.products || []).map(p => p._id);
        setSelectedProducts(allIds);
        setSelectAllFromDB(true);
        toast.success(`${allIds.length} produits sélectionnés`);
      }
    } catch (err) {
      toast.error('Erreur lors de la sélection');
    }
  };

  // Désélectionner tous les produits
  const deselectAllProducts = () => {
    setSelectedProducts([]);
    setSelectAllFromDB(false);
  };

  // Open product panel
  const openProductPanel = async (productId) => {
    try {
      const res = await fetch(`${API_URL}/api/sourcing/products/${productId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const product = await res.json();
        setSelectedProduct(product);
        setIsPanelOpen(true);
      }
    } catch (err) {
      toast.error('Erreur de chargement');
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-white">Accès Admin Requis</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/admin')} className="w-full">
              Aller au Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-200",
      isLight ? "bg-gray-100 admin-light-theme" : "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
    )}>
      {/* Sidebar */}
      <AdminSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : ''}`}>
        {/* Header */}
        <header className={cn(
          "border-b backdrop-blur-sm sticky top-0 z-40",
          isLight ? "border-gray-200 bg-white/90" : "border-zinc-800 bg-zinc-900/50"
        )}>
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AdminSidebarToggle onClick={() => setSidebarOpen(true)} />
              <div>
                <h1 className={cn("text-xl font-bold", isLight ? "text-gray-900" : "text-white")}>Sourcing & Import</h1>
                <p className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                  {/* Afficher le nombre filtré si un filtre est actif, sinon le total global */}
                  {(filters.status || filters.search || filters.imported_period || filters.imported_from) ? (
                    <>
                      <span className="text-mutagim-red font-medium">{totalProducts}</span> produits filtrés
                      <span className="text-gray-500"> (sur {stats.total_products || 0})</span>
                    </>
                  ) : (
                    <>{stats.total_products || 0} produits</>
                  )}
                  {' • '}{stats.needs_enrichment || 0} à enrichir
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={toggleTheme}
                className={cn(
                  "gap-2 transition-colors",
                  isLight 
                    ? "border-gray-300 bg-white hover:bg-gray-100 text-gray-700" 
                    : "border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                )}
              >
                {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span className="hidden sm:inline">{isLight ? 'Sombre' : 'Clair'}</span>
              </Button>
            </div>
          </div>
        </header>

      <main className="container mx-auto px-4 py-8">
        <div className="flex gap-6">
          {/* Main content */}
          <div className={`flex-1 transition-all ${isPanelOpen ? 'mr-[800px]' : ''}`}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className={cn(
                "p-1 grid grid-cols-3 w-full max-w-xl",
                isLight ? "bg-gray-200" : "bg-zinc-800/50"
              )}>
                <TabsTrigger value="import" className={cn(
                  "data-[state=active]:bg-mutagim-red data-[state=active]:text-white",
                  isLight ? "text-gray-700" : "text-white"
                )}>
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </TabsTrigger>
                <TabsTrigger value="catalog" className={cn(
                  "data-[state=active]:bg-mutagim-red data-[state=active]:text-white",
                  isLight ? "text-gray-700" : "text-white"
                )}>
                  <Package className="w-4 h-4 mr-2" />
                  Catalogue
                </TabsTrigger>
                <TabsTrigger value="margins" className={cn(
                  "data-[state=active]:bg-mutagim-red data-[state=active]:text-white",
                  isLight ? "text-gray-700" : "text-white"
                )}>
                  <Percent className="w-4 h-4 mr-2" />
                  Marges
                </TabsTrigger>
              </TabsList>

              {/* Tab: Import */}
              <TabsContent value="import" className="space-y-6">
                {/* Wizard Import Button */}
                <Card className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-purple-500/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                          <span className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">✨</span>
                          Import Guidé (5 étapes)
                        </h3>
                        <p className="text-gray-300 text-sm mt-1">
                          Assistant complet avec génération IA du contenu, import de photos et traductions automatiques
                        </p>
                      </div>
                      <Button 
                        onClick={() => navigate('/admin/sourcing/import')}
                        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                        data-testid="wizard-import-btn"
                      >
                        Lancer le Wizard
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Spreadsheet Import Button */}
                <Card className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-green-500/30">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                          <span className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">📊</span>
                          Import Google Spreadsheet
                        </h3>
                        <p className="text-gray-300 text-sm mt-1">
                          Importer depuis le spreadsheet communautaire ACBUY (Nike, Jordan, vêtements...)
                        </p>
                      </div>
                      <Button 
                        onClick={() => navigate('/admin/sourcing/spreadsheet')}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                        data-testid="spreadsheet-import-btn"
                      >
                        Import Spreadsheet
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Category Scraper - avec prévisualisation */}
                <Card className={cn(
                  "border",
                  isLight ? "bg-white border-gray-200" : "bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border-cyan-500/30"
                )}>
                  <CardHeader>
                    <CardTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                      <span className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white">🔍</span>
                      Scraper une Catégorie ACBUY.GG
                    </CardTitle>
                    <CardDescription className={isLight ? "text-gray-600" : "text-gray-300"}>
                      Analysez une page de catégorie, choisissez combien de produits importer
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Input URL + Bouton Analyser */}
                    {!categoryPreview && (
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://acbuy.gg/products.html?cat=Shoes"
                          value={categoryUrl}
                          onChange={(e) => setCategoryUrl(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !isScrapingCategory && categoryUrl.trim() && handleAnalyzeCategory()}
                          className={cn(
                            "flex-1",
                            isLight ? "bg-white border-gray-300" : "bg-zinc-800/50 border-zinc-700"
                          )}
                          data-testid="category-url-input"
                          disabled={isScrapingCategory}
                        />
                        <Button 
                          onClick={handleAnalyzeCategory}
                          disabled={isScrapingCategory || !categoryUrl.trim()}
                          className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                          data-testid="analyze-category-btn"
                        >
                          {isScrapingCategory && categoryScrapeJob?.status === 'analyzing' ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyse...
                            </>
                          ) : (
                            <>
                              <Search className="w-4 h-4 mr-2" />
                              Analyser
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {/* Prévisualisation avec choix de quantité */}
                    {categoryPreview && !categoryScrapeJob && (
                      <div className={cn(
                        "border rounded-lg p-4 space-y-4",
                        isLight ? "border-cyan-200 bg-cyan-50" : "border-cyan-500/30 bg-cyan-900/20"
                      )}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className={cn("font-semibold", isLight ? "text-gray-900" : "text-white")}>
                              {categoryPreview.total_products} produits trouvés
                            </h4>
                            <p className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                              Catégorie : {categoryPreview.category || 'Toutes'}
                            </p>
                          </div>
                          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                            Prêt à importer
                          </Badge>
                        </div>
                        
                        {/* Slider pour choisir la quantité */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className={cn("text-sm font-medium", isLight ? "text-gray-700" : "text-gray-300")}>
                              Nombre de produits à importer
                            </label>
                            <span className={cn("text-lg font-bold", isLight ? "text-cyan-600" : "text-cyan-400")}>
                              {categoryImportCount}
                            </span>
                          </div>
                          <input
                            type="range"
                            min="1"
                            max={categoryPreview.total_products}
                            value={categoryImportCount}
                            onChange={(e) => setCategoryImportCount(Number(e.target.value))}
                            className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                            data-testid="category-import-count-slider"
                          />
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>1</span>
                            <span>{Math.round(categoryPreview.total_products / 2)}</span>
                            <span>{categoryPreview.total_products}</span>
                          </div>
                        </div>
                        
                        {/* Aperçu des premiers produits */}
                        {categoryPreview.products_sample?.length > 0 && (
                          <div className="space-y-2">
                            <p className={cn("text-xs font-medium", isLight ? "text-gray-600" : "text-gray-400")}>
                              Aperçu des premiers produits :
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {categoryPreview.products_sample.slice(0, 5).map((p, idx) => (
                                <div key={idx} className={cn(
                                  "flex items-center gap-2 px-2 py-1 rounded text-xs",
                                  isLight ? "bg-white border" : "bg-zinc-800 border-zinc-700"
                                )}>
                                  {p.image && (
                                    <img src={p.image} alt="" className="w-6 h-6 object-cover rounded" />
                                  )}
                                  <span className={cn("truncate max-w-[120px]", isLight ? "text-gray-700" : "text-gray-300")}>
                                    {p.name || `Produit ${p.product_id}`}
                                  </span>
                                </div>
                              ))}
                              {categoryPreview.products_sample.length > 5 && (
                                <span className="text-xs text-gray-500">+{categoryPreview.products_sample.length - 5} autres...</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Boutons d'action */}
                        <div className="flex gap-2 pt-2">
                          <Button 
                            variant="outline" 
                            onClick={handleCancelCategoryPreview}
                            className={isLight ? "" : "border-zinc-600 hover:bg-zinc-800"}
                            data-testid="cancel-category-import-btn"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Annuler
                          </Button>
                          <Button 
                            onClick={handleConfirmCategoryImport}
                            className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                            data-testid="confirm-category-import-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Importer {categoryImportCount} produits
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Progression de l'import */}
                    {categoryScrapeJob && (
                      <div className={cn(
                        "border rounded-lg p-4 space-y-3",
                        isLight ? "border-gray-200 bg-gray-50" : "border-zinc-700 bg-zinc-800/50"
                      )}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {categoryScrapeJob.status === 'analyzing' && <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />}
                            {categoryScrapeJob.status === 'importing' && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
                            {categoryScrapeJob.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-400" />}
                            {categoryScrapeJob.status === 'failed' && <AlertTriangle className="w-4 h-4 text-red-400" />}
                            <span className={cn("font-medium", isLight ? "text-gray-700" : "text-white")}>
                              {categoryScrapeJob.status === 'analyzing' && 'Analyse de la page...'}
                              {categoryScrapeJob.status === 'importing' && 'Import en cours...'}
                              {categoryScrapeJob.status === 'completed' && 'Import terminé!'}
                              {categoryScrapeJob.status === 'failed' && 'Erreur'}
                            </span>
                          </div>
                          {categoryScrapeJob.status === 'completed' && categoryScrapeJob.result && (
                            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                              {categoryScrapeJob.result.imported} importés
                            </Badge>
                          )}
                        </div>
                        
                        {/* Barre de progression pour l'import */}
                        {categoryScrapeJob.status === 'importing' && categoryScrapeJob.import_progress && (
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs text-gray-400">
                              <span>Progression</span>
                              <span>{categoryScrapeJob.import_progress.current}/{categoryScrapeJob.import_progress.total}</span>
                            </div>
                            <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300"
                                style={{ width: `${(categoryScrapeJob.import_progress.current / categoryScrapeJob.import_progress.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Résumé final */}
                        {categoryScrapeJob.status === 'completed' && categoryScrapeJob.result && (
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div className={cn("p-2 rounded", isLight ? "bg-green-100" : "bg-green-900/30")}>
                              <div className="text-lg font-bold text-green-400">{categoryScrapeJob.result.imported}</div>
                              <div className="text-xs text-gray-400">Importés</div>
                            </div>
                            <div className={cn("p-2 rounded", isLight ? "bg-yellow-100" : "bg-yellow-900/30")}>
                              <div className="text-lg font-bold text-yellow-400">{categoryScrapeJob.result.duplicates}</div>
                              <div className="text-xs text-gray-400">Doublons</div>
                            </div>
                            <div className={cn("p-2 rounded", isLight ? "bg-red-100" : "bg-red-900/30")}>
                              <div className="text-lg font-bold text-red-400">{categoryScrapeJob.result.errors}</div>
                              <div className="text-xs text-gray-400">Erreurs</div>
                            </div>
                          </div>
                        )}
                        
                        {/* Bouton pour fermer */}
                        {(categoryScrapeJob.status === 'completed' || categoryScrapeJob.status === 'failed') && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setCategoryScrapeJob(null)}
                            className="w-full mt-2"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Fermer
                          </Button>
                        )}
                      </div>
                    )}
                    
                    <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>
                      <strong>Exemples :</strong> https://acbuy.gg/products.html?cat=Shoes, https://acbuy.gg/products.html?cat=T-Shirts
                    </p>
                  </CardContent>
                </Card>

                {/* LitBuy Re-enrichment Card */}
                <Card className={cn(
                  "border",
                  isLight ? "bg-white border-gray-200" : "bg-gradient-to-r from-orange-900/50 to-amber-900/50 border-orange-500/30"
                )}>
                  <CardHeader>
                    <CardTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                      <span className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center text-white">🔄</span>
                      Ré-enrichissement LitBuy/Weidian
                    </CardTitle>
                    <CardDescription className={isLight ? "text-gray-600" : "text-gray-300"}>
                      Corrige les produits LitBuy avec données incomplètes (variantes, descriptions IA, images)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* No job running - Show start button */}
                    {(!litbuyReenrichJob || litbuyReenrichJob.status === 'completed') && (
                      <div className="flex items-center justify-between">
                        <div className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-300")}>
                          Lance le scraping Weidian + génération IA pour tous les produits LitBuy sans données complètes.
                        </div>
                        <Button 
                          onClick={handleStartLitbuyReenrich}
                          disabled={isStartingReenrich}
                          className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                          data-testid="litbuy-reenrich-btn"
                        >
                          {isStartingReenrich ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Lancement...
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-4 h-4 mr-2" />
                              Lancer le ré-enrichissement
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    
                    {/* Job running - Show progress */}
                    {litbuyReenrichJob && litbuyReenrichJob.status === 'running' && (
                      <div className={cn(
                        "border rounded-lg p-4 space-y-3",
                        isLight ? "border-gray-200 bg-gray-50" : "border-zinc-700 bg-zinc-800/50"
                      )}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-orange-400" />
                            <span className={cn("font-medium", isLight ? "text-gray-700" : "text-white")}>
                              Ré-enrichissement en cours...
                            </span>
                          </div>
                          <Badge variant="outline" className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                            {litbuyReenrichJob.processed}/{litbuyReenrichJob.total}
                          </Badge>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs text-gray-400">
                            <span>Progression</span>
                            <span>{Math.round((litbuyReenrichJob.processed / litbuyReenrichJob.total) * 100)}%</span>
                          </div>
                          <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all duration-300"
                              style={{ width: `${(litbuyReenrichJob.processed / litbuyReenrichJob.total) * 100}%` }}
                            />
                          </div>
                        </div>
                        
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className={cn("p-2 rounded", isLight ? "bg-blue-100" : "bg-blue-900/30")}>
                            <div className="text-lg font-bold text-blue-400">{litbuyReenrichJob.processed}</div>
                            <div className="text-xs text-gray-400">Traités</div>
                          </div>
                          <div className={cn("p-2 rounded", isLight ? "bg-green-100" : "bg-green-900/30")}>
                            <div className="text-lg font-bold text-green-400">{litbuyReenrichJob.enriched}</div>
                            <div className="text-xs text-gray-400">Enrichis</div>
                          </div>
                          <div className={cn("p-2 rounded", isLight ? "bg-red-100" : "bg-red-900/30")}>
                            <div className="text-lg font-bold text-red-400">{litbuyReenrichJob.failed}</div>
                            <div className="text-xs text-gray-400">Échecs</div>
                          </div>
                        </div>
                        
                        {/* Recent logs */}
                        {litbuyReenrichJob.logs && litbuyReenrichJob.logs.length > 0 && (
                          <div className={cn(
                            "mt-2 p-2 rounded text-xs font-mono max-h-32 overflow-y-auto",
                            isLight ? "bg-gray-100 text-gray-600" : "bg-zinc-900 text-gray-400"
                          )}>
                            {litbuyReenrichJob.logs.slice(-5).map((log, idx) => (
                              <div key={idx} className="truncate">{log}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>
                      <strong>Note :</strong> Chaque produit prend ~30 secondes (scraping Weidian + 3 appels Claude IA)
                    </p>
                  </CardContent>
                </Card>

                {/* Import URL - Multi-URL avec journal */}
                <Card className={cn(
                  "border",
                  isLight ? "bg-white border-gray-200" : "bg-zinc-900/60 border-zinc-800"
                )}>
                  <CardHeader>
                    <CardTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
                      <Link2 className="w-5 h-5" />
                      Import par URL
                    </CardTitle>
                    <CardDescription className={isLight ? "text-gray-600" : ""}>
                      Ajoutez plusieurs URLs ACBUY puis lancez l'import en lot
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Input + Boutons */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://www.acbuy.com/product?id=..."
                        value={importUrl}
                        onChange={(e) => setImportUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addUrlToList()}
                        className={cn(
                          "flex-1",
                          isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700"
                        )}
                        data-testid="import-url-input"
                        disabled={isImporting}
                      />
                      <Button 
                        onClick={addUrlToList} 
                        disabled={isImporting || !importUrl.trim()}
                        variant="outline"
                        className={cn(
                          isLight ? "border-gray-300 hover:bg-gray-100" : "border-zinc-700 hover:bg-zinc-700"
                        )}
                        data-testid="add-url-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter
                      </Button>
                      <Button 
                        onClick={() => setShowMultiUrlModal(true)} 
                        disabled={isImporting}
                        variant="outline"
                        className={cn(
                          "bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-500/50 hover:border-amber-400",
                          isLight ? "text-amber-700" : "text-amber-400"
                        )}
                        data-testid="multi-url-btn"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Coller plusieurs URLs
                      </Button>
                    </div>
                    
                    {/* Liste des URLs à importer */}
                    {importUrls.length > 0 && (
                      <div className={cn(
                        "border rounded-lg overflow-hidden",
                        isLight ? "border-gray-200" : "border-zinc-700"
                      )}>
                        <div className={cn(
                          "px-3 py-2 flex items-center justify-between",
                          isLight ? "bg-gray-50 border-b border-gray-200" : "bg-zinc-800/50 border-b border-zinc-700"
                        )}>
                          <span className={cn("text-sm font-medium", isLight ? "text-gray-700" : "text-gray-300")}>
                            {importUrls.length} URL(s) en attente
                          </span>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={clearUrlList}
                              disabled={isImporting}
                              className={cn(
                                "h-7 text-xs",
                                isLight ? "text-gray-600 hover:text-red-600" : "text-gray-400 hover:text-red-400"
                              )}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Vider
                            </Button>
                            <Button 
                              size="sm"
                              onClick={handleImportAllUrls}
                              disabled={isImporting}
                              className="h-7 bg-green-600 hover:bg-green-700 text-xs"
                              data-testid="import-all-btn"
                            >
                              {isImporting ? (
                                <>
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  {importProgress.current}/{importProgress.total}
                                </>
                              ) : (
                                <>
                                  <Upload className="w-3 h-3 mr-1" />
                                  Importer tout
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {importUrls.map((urlItem, idx) => (
                            <div 
                              key={urlItem.id}
                              className={cn(
                                "px-3 py-2 flex items-center gap-3 text-sm border-b last:border-b-0",
                                isLight ? "border-gray-100 hover:bg-gray-50" : "border-zinc-800 hover:bg-zinc-800/50"
                              )}
                            >
                              <span className={cn("w-6 text-center font-mono text-xs", isLight ? "text-gray-400" : "text-gray-500")}>
                                {idx + 1}
                              </span>
                              
                              {/* Status icon */}
                              {urlItem.status === 'pending' && <Clock className="w-4 h-4 text-gray-400" />}
                              {urlItem.status === 'importing' && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
                              {urlItem.status === 'success' && <CheckCircle className="w-4 h-4 text-green-400" />}
                              {urlItem.status === 'error' && <AlertTriangle className="w-4 h-4 text-red-400" />}
                              
                              <span className={cn(
                                "flex-1 truncate font-mono text-xs",
                                urlItem.status === 'success' && "text-green-400",
                                urlItem.status === 'error' && "text-red-400",
                                urlItem.status === 'pending' && (isLight ? "text-gray-600" : "text-gray-300"),
                                urlItem.status === 'importing' && "text-blue-400"
                              )}>
                                {urlItem.productName || urlItem.url}
                              </span>
                              
                              {urlItem.status === 'pending' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeUrlFromList(urlItem.id)}
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Journal de progression */}
                    {importProgress.logs.length > 0 && (
                      <div className={cn(
                        "border rounded-lg overflow-hidden",
                        isLight ? "border-gray-200" : "border-zinc-700"
                      )}>
                        <div className={cn(
                          "px-3 py-2 flex items-center justify-between",
                          isLight ? "bg-gray-50 border-b border-gray-200" : "bg-zinc-800/50 border-b border-zinc-700"
                        )}>
                          <span className={cn("text-sm font-medium flex items-center gap-2", isLight ? "text-gray-700" : "text-gray-300")}>
                            📋 Journal de progression
                            {importProgress.status === 'running' && (
                              <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">
                                En cours
                              </Badge>
                            )}
                            {importProgress.status === 'completed' && (
                              <Badge variant="outline" className="text-xs bg-green-500/20 text-green-400 border-green-500/30">
                                Terminé
                              </Badge>
                            )}
                            {importProgress.status === 'error' && (
                              <Badge variant="outline" className="text-xs bg-red-500/20 text-red-400 border-red-500/30">
                                Erreur
                              </Badge>
                            )}
                          </span>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => setImportProgress(prev => ({ ...prev, logs: [] }))}
                            className={cn(
                              "h-7 text-xs",
                              isLight ? "text-gray-600" : "text-gray-400"
                            )}
                          >
                            Effacer
                          </Button>
                        </div>
                        <div className={cn(
                          "max-h-52 overflow-y-auto p-3 font-mono text-xs space-y-1",
                          isLight ? "bg-gray-50" : "bg-zinc-900"
                        )}>
                          {importProgress.logs.map((log, idx) => (
                            <div 
                              key={idx}
                              className={cn(
                                "flex gap-2",
                                log.type === 'error' && "text-red-400",
                                log.type === 'warning' && "text-yellow-400",
                                log.type === 'success' && "text-green-400",
                                log.type === 'step' && "text-blue-400",
                                log.type === 'info' && (isLight ? "text-gray-600" : "text-gray-400")
                              )}
                            >
                              <span className="opacity-60">[{log.timestamp}]</span>
                              <span>{log.message}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Options */}
                    <div className={cn(
                      "grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t",
                      isLight ? "border-gray-200" : "border-zinc-700"
                    )}>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={importOptions.as_draft}
                          onCheckedChange={(c) => setImportOptions({...importOptions, as_draft: c})}
                        />
                        <Label className={cn("text-sm", isLight ? "text-gray-700" : "text-white")}>Brouillon</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={importOptions.generate_ai_name}
                          onCheckedChange={(c) => setImportOptions({...importOptions, generate_ai_name: c})}
                        />
                        <Label className={cn("text-sm", isLight ? "text-gray-700" : "text-white")}>Nom IA</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={importOptions.generate_ai_description}
                          onCheckedChange={(c) => setImportOptions({...importOptions, generate_ai_description: c})}
                        />
                        <Label className={cn("text-sm", isLight ? "text-gray-700" : "text-white")}>Description IA</Label>
                      </div>
                    </div>
                    
                    {/* Info box about scraping limitations */}
                    <div className={cn(
                      "p-3 rounded-lg",
                      isLight ? "bg-yellow-50 border border-yellow-200" : "bg-yellow-500/10 border border-yellow-500/30"
                    )}>
                      <p className={cn("text-sm", isLight ? "text-yellow-800" : "text-yellow-200")}>
                        <strong>Note :</strong> Le scraping automatique peut ne pas fonctionner sur tous les sites. 
                        Si les données ne sont pas correctes, vous pouvez modifier le produit après import ou utiliser l'import manuel ci-dessous.
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Import Manuel */}
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-500" />
                      Création Manuelle
                    </CardTitle>
                    <CardDescription>
                      Créez un produit en saisissant les informations directement
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white">Nom du produit *</Label>
                        <Input
                          placeholder="Ex: Jordan 1 Retro High OG Chicago"
                          value={manualProduct?.name || ''}
                          onChange={(e) => setManualProduct({...manualProduct, name: e.target.value})}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Prix de vente (ILS) *</Label>
                        <Input
                          type="number"
                          placeholder="Ex: 450"
                          value={manualProduct?.price || ''}
                          onChange={(e) => setManualProduct({...manualProduct, price: e.target.value})}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">URL source (optionnel)</Label>
                        <Input
                          placeholder="https://..."
                          value={manualProduct?.source_url || ''}
                          onChange={(e) => setManualProduct({...manualProduct, source_url: e.target.value})}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Prix source CNY (optionnel)</Label>
                        <Input
                          type="number"
                          placeholder="Ex: 100"
                          value={manualProduct?.source_price || ''}
                          onChange={(e) => setManualProduct({...manualProduct, source_price: e.target.value})}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Description</Label>
                      <textarea
                        placeholder="Description du produit..."
                        value={manualProduct?.description || ''}
                        onChange={(e) => setManualProduct({...manualProduct, description: e.target.value})}
                        className="w-full h-24 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder:text-gray-400"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">URLs des images (une par ligne)</Label>
                      <textarea
                        placeholder={"https://image1.jpg\nhttps://image2.jpg"}
                        value={manualProduct?.images || ''}
                        onChange={(e) => setManualProduct({...manualProduct, images: e.target.value})}
                        className="w-full h-20 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder:text-gray-400 font-mono text-sm"
                      />
                    </div>
                    <Button 
                      onClick={async () => {
                        if (!manualProduct?.name || !manualProduct?.price) {
                          toast.error('Le nom et le prix sont requis');
                          return;
                        }
                        setIsImporting(true);
                        try {
                          const res = await fetch(`${API_URL}/api/sourcing/create-manual`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              name: manualProduct.name,
                              price: parseFloat(manualProduct.price),
                              source_url: manualProduct.source_url || null,
                              source_price: manualProduct.source_price ? parseFloat(manualProduct.source_price) : null,
                              description: manualProduct.description || null,
                              images: manualProduct.images ? manualProduct.images.split('\n').filter(u => u.trim()) : [],
                              status: importOptions.as_draft ? 'draft' : 'published'
                            })
                          });
                          const data = await res.json();
                          if (res.ok) {
                            toast.success('Produit créé avec succès !');
                            setManualProduct({});
                            fetchData(token);
                          } else {
                            toast.error(data.detail || 'Erreur lors de la création');
                          }
                        } catch (err) {
                          toast.error('Erreur de connexion');
                        } finally {
                          setIsImporting(false);
                        }
                      }}
                      disabled={isImporting || !manualProduct?.name || !manualProduct?.price}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {isImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Créer le produit
                    </Button>
                  </CardContent>
                </Card>

                {/* Import CSV */}
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5" />
                      Import fichier CSV
                    </CardTitle>
                    <CardDescription>
                      Uploadez un fichier CSV avec une colonne "url"
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center">
                      <Upload className="w-12 h-12 mx-auto mb-4 text-gray-200" />
                      <p className="text-white mb-4">Glissez un fichier CSV ici ou</p>
                      <input
                        type="file"
                        accept=".csv,.txt"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="csv-upload"
                        data-testid="csv-upload-input"
                      />
                      <label htmlFor="csv-upload">
                        <Button as="span" variant="outline" className="cursor-pointer">
                          Parcourir
                        </Button>
                      </label>
                    </div>
                  </CardContent>
                </Card>

                {/* Import Google Sheets / Google Drive */}
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Table className="w-5 h-5 text-green-500" />
                      Import Google Sheets
                    </CardTitle>
                    <CardDescription>
                      Importez vos produits depuis un Google Sheets partagé ou Google Drive
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Google Sheets URL */}
                    <div className="space-y-2">
                      <Label className="text-white flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.5 3H4.5C3.12 3 2 4.12 2 5.5v13C2 19.88 3.12 21 4.5 21h15c1.38 0 2.5-1.12 2.5-2.5v-13C22 4.12 20.88 3 19.5 3zM4.5 5h15c.28 0 .5.22.5.5V8H4V5.5c0-.28.22-.5.5-.5zM4 19v-9h16v9.5c0 .28-.22.5-.5.5H4.5c-.28 0-.5-.22-.5-.5V19z" fill="#34A853"/>
                          <path d="M6 11h4v2H6v-2zm0 4h4v2H6v-2zm6-4h6v2h-6v-2zm0 4h6v2h-6v-2z" fill="#34A853"/>
                        </svg>
                        Lien Google Sheets
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="https://docs.google.com/spreadsheets/d/..."
                          value={googleSheetsUrl}
                          onChange={(e) => setGoogleSheetsUrl(e.target.value)}
                          className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                          data-testid="google-sheets-url-input"
                        />
                        <Button 
                          onClick={async () => {
                            if (!googleSheetsUrl.includes('docs.google.com/spreadsheets')) {
                              toast.error('URL Google Sheets invalide');
                              return;
                            }
                            setIsImportingSheets(true);
                            try {
                              const res = await fetch(`${API_URL}/api/sourcing/import/google-sheets`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ 
                                  url: googleSheetsUrl,
                                  options: importOptions 
                                })
                              });
                              const data = await res.json();
                              if (res.ok) {
                                toast.success(`${data.imported_count || 0} produits importés depuis Google Sheets`);
                                setGoogleSheetsUrl('');
                                fetchData(token);
                              } else {
                                toast.error(data.detail || 'Erreur lors de l\'import');
                              }
                            } catch (err) {
                              toast.error('Erreur de connexion');
                            } finally {
                              setIsImportingSheets(false);
                            }
                          }}
                          disabled={isImportingSheets || !googleSheetsUrl}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="import-sheets-btn"
                        >
                          {isImportingSheets ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Importer'}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-300">
                        Le fichier doit être partagé en lecture publique et contenir les colonnes : url, name, price
                      </p>
                    </div>
                    
                    {/* Google Drive Button */}
                    <div className="pt-4 border-t border-zinc-700">
                      <Label className="text-white mb-2 block">Ou sélectionnez depuis Google Drive</Label>
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-center gap-3 py-6 border-zinc-600 hover:bg-zinc-800"
                        onClick={() => {
                          toast.info('Connexion à Google Drive...', { duration: 2000 });
                          // Open Google Drive picker (requires Google API integration)
                          window.open('https://drive.google.com/drive/my-drive', '_blank');
                        }}
                        data-testid="google-drive-btn"
                      >
                        <svg className="w-6 h-6" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                          <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                          <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
                          <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
                          <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                          <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                          <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                        </svg>
                        <span className="text-white">Ouvrir Google Drive</span>
                      </Button>
                      <p className="text-xs text-gray-300 mt-2 text-center">
                        Copiez le lien de partage de votre fichier Google Sheets depuis Drive
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Sources supportées */}
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white text-base">Sources supportées</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {['ACBuy', 'KakoBuy', 'CNFans', 'Sugargoo', 'Pandabuy', 'CSSBuy'].map(source => (
                        <Badge key={source} variant="secondary" className="bg-zinc-700">
                          {source}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-gray-200 mt-3">
                      Les liens d'agents (ACBUY, KakoBuy) sont automatiquement convertis en liens source.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tab: Catalog */}
              <TabsContent value="catalog" className="space-y-4">
                {/* Filters */}
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white" />
                    <Input
                      placeholder="Rechercher..."
                      value={filters.search}
                      onChange={(e) => setFilters({...filters, search: e.target.value})}
                      onKeyDown={(e) => e.key === 'Enter' && fetchProducts()}
                      className="pl-10 bg-zinc-800 border-zinc-700"
                      data-testid="search-input"
                    />
                  </div>
                  
                  <Select
                    value={filters.status || 'all'}
                    onValueChange={(v) => {
                      setFilters({...filters, status: v === 'all' ? '' : v});
                    }}
                  >
                    <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="draft">Brouillons</SelectItem>
                      <SelectItem value="published">Publiés</SelectItem>
                      <SelectItem value="archived">Archivés</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={filters.imported_period || 'all'}
                    onValueChange={(v) => {
                      setFilters({...filters, imported_period: v === 'all' ? '' : v});
                    }}
                  >
                    <SelectTrigger className="w-44 bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Date d'import" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les imports</SelectItem>
                      <SelectItem value="today">Aujourd'hui</SelectItem>
                      <SelectItem value="yesterday">Hier</SelectItem>
                      <SelectItem value="7d">7 derniers jours</SelectItem>
                      <SelectItem value="14d">14 derniers jours</SelectItem>
                      <SelectItem value="30d">30 derniers jours</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button 
                    variant={filters.needs_enrichment ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setFilters({...filters, needs_enrichment: filters.needs_enrichment ? null : true});
                    }}
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    À enrichir
                  </Button>
                  
                  <Button variant="outline" size="sm" onClick={() => fetchProducts()}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCleanupImages}
                    disabled={isCleaningImages}
                    title="Nettoyer les images invalides"
                    data-testid="cleanup-images-btn"
                  >
                    {isCleaningImages ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Image className="w-4 h-4" />
                    )}
                    <span className="ml-2 hidden sm:inline">Nettoyer images</span>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={async () => {
                      try {
                        toast.info('Assignation des catégories marques en cours...');
                        const res = await fetch(`${API_URL}/api/sourcing/products/auto-assign-brand-categories`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          }
                        });
                        const data = await res.json();
                        if (data.success) {
                          toast.success(`${data.updated_count} produits assignés à leurs catégories marques`);
                          fetchProducts();
                        } else {
                          toast.error(data.detail || 'Erreur');
                        }
                      } catch (err) {
                        toast.error('Erreur lors de l\'assignation');
                      }
                    }}
                    title="Assigner automatiquement les produits aux catégories marques"
                    data-testid="auto-assign-brands-btn"
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  >
                    <Tags className="w-4 h-4" />
                    <span className="ml-2 hidden sm:inline">Auto-assigner marques</span>
                  </Button>
                </div>

                {/* Last scrape info + Bulk rescrape progress */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  {lastScrapeInfo && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <span className={`w-2 h-2 rounded-full ${lastScrapeInfo.status === 'completed' ? 'bg-green-500' : lastScrapeInfo.status === 'running' ? 'bg-yellow-500 animate-pulse' : 'bg-gray-500'}`} />
                      <span>
                        Dernier scrape: {lastScrapeInfo.started_at ? new Date(lastScrapeInfo.started_at).toLocaleString('fr-FR', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}) : '-'}
                        {lastScrapeInfo.new_items > 0 && <span className="text-green-400 ml-1">+{lastScrapeInfo.new_items} nouveaux</span>}
                      </span>
                    </div>
                  )}
                  
                  {/* Batch rescrape broken images status */}
                  {batchRescrapeStatus?.status === 'running' && (
                    <div className="flex items-center gap-3 p-2 bg-blue-500/20 rounded-lg border border-blue-500/30 animate-pulse">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                      <div className="text-sm">
                        <span className="text-blue-400 font-medium">Réparation images</span>
                        <span className="text-white ml-2 font-bold">{batchRescrapeStatus.completed}/{batchRescrapeStatus.total}</span>
                        <span className="text-green-400 ml-2">✓{batchRescrapeStatus.success}</span>
                        {batchRescrapeStatus.errors > 0 && <span className="text-red-400 ml-1">✗{batchRescrapeStatus.errors}</span>}
                      </div>
                      <div className="w-40 h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all" 
                          style={{width: `${(batchRescrapeStatus.completed / batchRescrapeStatus.total) * 100}%`}}
                        />
                      </div>
                      <span className="text-xs text-gray-400">{batchRescrapeStatus.current_product?.substring(0, 20)}...</span>
                    </div>
                  )}
                  
                  {batchRescrapeStatus?.status === 'completed' && batchRescrapeStatus.finished_at && !hiddenStatuses.batchRescrape && (
                    <div className="flex items-center gap-2 text-sm text-green-400">
                      <span className="w-2 h-2 rounded-full bg-green-500" />
                      <span>
                        Réparation terminée: {batchRescrapeStatus.success}/{batchRescrapeStatus.total} produits
                        {batchRescrapeStatus.errors > 0 && <span className="text-red-400 ml-1">({batchRescrapeStatus.errors} erreurs)</span>}
                      </span>
                      <button 
                        onClick={() => setHiddenStatuses(prev => ({...prev, batchRescrape: true}))}
                        className="ml-2 p-1 hover:bg-zinc-700 rounded text-gray-400 hover:text-white transition-colors"
                        title="Fermer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Telegram bulk import progress */}
                  {telegramImportJob?.status === 'running' && (
                    <div className="flex items-center gap-3 p-2 bg-purple-500/20 rounded-lg border border-purple-500/30 animate-pulse">
                      <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                      <div className="text-sm">
                        <span className="text-purple-400 font-medium">Import Telegram</span>
                        <span className="text-white ml-2 font-bold">{telegramImportJob.processed}/{telegramImportJob.total}</span>
                        <span className="text-green-400 ml-2">✓{telegramImportJob.imported}</span>
                        {telegramImportJob.errors > 0 && <span className="text-red-400 ml-1">✗{telegramImportJob.errors}</span>}
                      </div>
                      <div className="w-40 h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all" 
                          style={{width: `${(telegramImportJob.processed / telegramImportJob.total) * 100}%`}}
                        />
                      </div>
                    </div>
                  )}
                  
                  {telegramImportJob?.status === 'completed' && telegramImportJob.completed_at && !hiddenStatuses.telegramImport && (
                    <div className="flex items-center gap-2 text-sm text-purple-400">
                      <span className="w-2 h-2 rounded-full bg-purple-500" />
                      <span>
                        Import terminé: {telegramImportJob.imported}/{telegramImportJob.total} produits
                        {telegramImportJob.errors > 0 && <span className="text-red-400 ml-1">({telegramImportJob.errors} erreurs)</span>}
                      </span>
                      <button 
                        onClick={() => setHiddenStatuses(prev => ({...prev, telegramImport: true}))}
                        className="ml-2 p-1 hover:bg-zinc-700 rounded text-gray-400 hover:text-white transition-colors"
                        title="Fermer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  
                  {/* Telegram import interrupted */}
                  {telegramImportJob?.status === 'interrupted' && telegramImportJob.processed < telegramImportJob.total && (
                    <div className="flex items-center gap-2 p-2 bg-orange-500/20 rounded-lg border border-orange-500/30">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      <span className="text-orange-400 text-sm">
                        Import interrompu : {telegramImportJob.processed}/{telegramImportJob.total}
                      </span>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-red-400 hover:bg-red-500/10 h-6 px-2"
                        onClick={async () => {
                          if (confirm('Supprimer ce job ?')) {
                            await fetch(`${API_URL}/api/telegram/import-job/${telegramImportJob.job_id}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            setTelegramImportJob(null);
                          }
                        }}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-orange-600 hover:bg-orange-700 h-6 px-2"
                        onClick={async () => {
                          const res = await fetch(`${API_URL}/api/telegram/import-job/${telegramImportJob.job_id}/resume`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (res.ok) {
                            setTelegramImportJob({...telegramImportJob, status: 'running'});
                            toast.success('Import repris');
                          }
                        }}
                      >
                        Reprendre
                      </Button>
                    </div>
                  )}
                  
                  {bulkRescrapeJob?.status === 'running' && (
                    <div className="flex items-center gap-3 p-2 bg-orange-500/20 rounded-lg border border-orange-500/30">
                      <Loader2 className="w-4 h-4 animate-spin text-orange-400" />
                      <div className="text-sm">
                        <span className="text-orange-400 font-medium">Rescrape en cours</span>
                        <span className="text-gray-300 ml-2">{bulkRescrapeJob.completed}/{bulkRescrapeJob.total}</span>
                        <span className="text-gray-400 ml-2 text-xs">{bulkRescrapeJob.current?.substring(0, 30)}</span>
                      </div>
                      <div className="w-32 h-2 bg-zinc-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-500 transition-all" 
                          style={{width: `${(bulkRescrapeJob.completed / bulkRescrapeJob.total) * 100}%`}}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Bulk actions */}
                {selectedProducts.length > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-zinc-800/50 rounded-lg">
                    <span className="text-sm text-white">{selectedProducts.length} sélectionné(s)</span>
                    <Button 
                      size="sm" 
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                      onClick={handleBulkRescrape}
                      disabled={bulkRescrapeJob?.status === 'running'}
                    >
                      <RefreshCw className={`w-4 h-4 mr-1 ${bulkRescrapeJob?.status === 'running' ? 'animate-spin' : ''}`} />
                      Rescraper
                    </Button>
                    <Button 
                      size="sm" 
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      onClick={() => setShowAiImageGenerator(true)}
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      Générer visuels IA
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      onClick={() => setShowCategoryDialog(true)}
                    >
                      <FolderOpen className="w-4 h-4 mr-1" />
                      Catégoriser
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      onClick={() => setShowMarginDialog(true)}
                    >
                      <Percent className="w-4 h-4 mr-1" />
                      Appliquer marge
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('publish')}>
                      Publier
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('archive')}>
                      Archiver
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleBulkAction('delete')}>
                      Supprimer
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedProducts([])}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Products list */}
                <Card className={cn(
                  "border",
                  isLight ? "bg-white border-gray-200" : "bg-zinc-900/60 border-zinc-800"
                )}>
                  {/* Barre d'info de sélection */}
                  {selectedProducts.length > 0 && (
                    <div className={cn(
                      "px-4 py-2 border-b flex items-center justify-between",
                      isLight ? "bg-blue-50 border-blue-200" : "bg-blue-900/30 border-zinc-700"
                    )}>
                      <div className="flex items-center gap-3">
                        <span className={cn("text-sm font-medium", isLight ? "text-blue-700" : "text-blue-300")}>
                          {selectedProducts.length} produit(s) sélectionné(s)
                          {!selectAllFromDB && selectedProducts.length < totalProducts && (
                            <span className="text-gray-400 ml-1">(sur {totalProducts} au total)</span>
                          )}
                        </span>
                        
                        {/* Bouton pour sélectionner tous les produits de la DB */}
                        {!selectAllFromDB && selectedProducts.length < totalProducts && areAllCurrentPageSelected() && (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={selectAllProductsFromDB}
                            className={cn(
                              "text-xs p-0 h-auto",
                              isLight ? "text-blue-600 hover:text-blue-800" : "text-blue-400 hover:text-blue-300"
                            )}
                          >
                            Sélectionner les {totalProducts} produits
                          </Button>
                        )}
                        
                        {/* Bouton pour désélectionner tout */}
                        <Button
                          variant="link"
                          size="sm"
                          onClick={deselectAllProducts}
                          className={cn(
                            "text-xs p-0 h-auto",
                            isLight ? "text-gray-600 hover:text-gray-800" : "text-gray-400 hover:text-gray-300"
                          )}
                        >
                          Désélectionner tout
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBulkRescrape}
                          disabled={bulkRescrapeJob?.status === 'running'}
                          className={cn(
                            "text-xs",
                            isLight ? "border-green-300 text-green-700 hover:bg-green-50" : "border-green-600 text-green-400 hover:bg-green-900/30"
                          )}
                        >
                          <RefreshCw className={cn("w-3 h-3 mr-1", bulkRescrapeJob?.status === 'running' && "animate-spin")} />
                          {bulkRescrapeJob?.status === 'running' ? `Réimportation... (${bulkRescrapeJob.completed}/${bulkRescrapeJob.total})` : "Réimporter la sélection"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedProducts([])}
                          className={cn("text-xs", isLight ? "text-blue-600 hover:text-blue-800" : "text-blue-400 hover:text-blue-200")}
                        >
                          Tout désélectionner
                        </Button>
                      </div>
                    </div>
                  )}
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead className={cn(
                        "border-b",
                        isLight ? "border-gray-200 bg-gray-50" : "border-zinc-700 bg-zinc-800/50"
                      )}>
                        <tr className={cn("text-sm", isLight ? "text-gray-900" : "text-white")}>
                          <th className="p-3 text-left">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={areAllCurrentPageSelected()}
                                onCheckedChange={selectAllProducts}
                              />
                              <span className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>
                                ({products.length})
                              </span>
                            </div>
                          </th>
                          <th className="p-3 text-center font-medium cursor-pointer hover:text-red-400" onClick={() => handleSortByDate()}>
                            Date {sortField === 'date' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                          </th>
                          <th className="p-3 text-left font-medium">Produit</th>
                          <th className="p-3 text-left font-medium">Source</th>
                          <th className="p-3 text-center font-medium cursor-pointer hover:text-red-400" onClick={() => handleSortBySizes()}>
                            📏 Tailles {sortField === 'sizes' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                          </th>
                          <th className="p-3 text-center font-medium cursor-pointer hover:text-red-400" onClick={() => handleSortByColors()}>
                            🎨 Couleurs {sortField === 'colors' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                          </th>
                          <th className="p-3 text-right font-medium cursor-pointer hover:text-red-400" onClick={() => handleSortByPrice()}>
                            Prix achat {sortField === 'price' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                          </th>
                          <th className="p-3 text-right font-medium cursor-pointer hover:text-red-400" onClick={() => handleSortBySellingPrice()}>
                            Prix vente {sortField === 'selling_price' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                          </th>
                          <th className="p-3 text-right font-medium cursor-pointer hover:text-red-400" onClick={() => handleSortByMargin()}>
                            Marge {sortField === 'margin' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                          </th>
                          <th className="p-3 text-center font-medium cursor-pointer hover:text-red-400" onClick={() => handleSortByStatus()}>
                            Statut {sortField === 'status' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                          </th>
                          <th className="p-3 text-center font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.length === 0 ? (
                          <tr>
                            <td colSpan={11} className="p-8 text-center text-gray-200">
                              Aucun produit trouvé
                            </td>
                          </tr>
                        ) : products.map(product => (
                          <tr 
                            key={product._id} 
                            className="border-b border-zinc-800 hover:bg-zinc-800/30 cursor-pointer"
                            onClick={() => openProductPanel(product._id)}
                          >
                            <td className="p-3" onClick={e => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedProducts.includes(product._id)}
                                onCheckedChange={() => toggleProductSelection(product._id)}
                              />
                            </td>
                            <td className="p-3 text-center text-xs text-gray-300">
                              {product.created_at ? new Date(product.created_at).toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit'}) : '-'}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-zinc-800 rounded overflow-hidden flex items-center justify-center">
                                  {(product.images?.main?.[0] || product.images?.[0]) ? (
                                    <img 
                                      src={getImageUrl(product.images?.main?.[0] || product.images?.[0])} 
                                      alt="" 
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/placeholder.png';
                                        e.target.className = 'w-8 h-8 opacity-30';
                                      }}
                                    />
                                  ) : (
                                    <Package className="w-6 h-6 text-zinc-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="text-white font-medium text-sm line-clamp-1">
                                    {product.name?.fr || 'Sans nom'}
                                  </p>
                                  <p className="text-xs text-gray-200">{product.sku}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <Badge variant="outline" className="text-xs">
                                {product.source_type || 'Manuel'}
                              </Badge>
                            </td>
                            {/* Tailles - nombre de variantes */}
                            <td className="p-3 text-center">
                              <span className={`text-sm font-medium ${(product.variants?.length || 0) > 0 ? 'text-blue-400' : 'text-gray-500'}`}>
                                {product.variants?.length || 0}
                              </span>
                            </td>
                            {/* Couleurs - nombre d'images variantes */}
                            <td className="p-3 text-center">
                              <span className={`text-sm font-medium ${(product.images?.variants?.length || 0) > 0 ? 'text-orange-400' : 'text-gray-500'}`}>
                                {product.images?.variants?.length || 0}
                              </span>
                            </td>
                            {/* Prix achat - 3 devises (même ordre que prix vente) */}
                            <td className="p-3 text-right">
                              <p className="text-blue-400 font-medium text-sm">
                                ${product.source_price?.price_usd?.toFixed(2) || '—'}
                              </p>
                              <p className="text-xs text-gray-400">
                                €{product.source_price?.price_usd ? (product.source_price.price_usd * 0.92).toFixed(2) : '—'}
                              </p>
                              <p className="text-xs text-gray-400">
                                ₪{product.source_price?.price_usd ? (product.source_price.price_usd * 3.65).toFixed(2) : '—'}
                              </p>
                            </td>
                            {/* Prix vente - 3 devises (même ordre que prix achat) */}
                            <td className="p-3 text-right">
                              <p className="text-white font-medium text-sm">
                                ${typeof product.pricing?.prices?.USD === 'object'
                                  ? product.pricing.prices.USD.amount?.toFixed(2)
                                  : product.pricing?.prices?.USD?.toFixed(2) || '—'}
                              </p>
                              <p className="text-xs text-gray-400">
                                €{typeof product.pricing?.prices?.EUR === 'object'
                                  ? product.pricing.prices.EUR.amount?.toFixed(2)
                                  : product.pricing?.prices?.EUR?.toFixed(2) || '—'}
                              </p>
                              <p className="text-xs text-gray-400">
                                ₪{typeof product.pricing?.prices?.ILS === 'object' 
                                  ? product.pricing.prices.ILS.amount?.toFixed(2)
                                  : product.pricing?.prices?.ILS?.toFixed(2) || product.price?.amount?.toFixed(2) || '—'}
                              </p>
                            </td>
                            <td className="p-3 text-right">
                              <span className={`text-sm ${product.margin_percent > 100 ? 'text-green-400' : 'text-yellow-400'}`}>
                                {product.margin_percent?.toFixed(0)}%
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <Badge className={
                                product.status === 'published' ? 'bg-green-500/20 text-green-400' :
                                product.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                                product.status === 'archived' ? 'bg-red-500/20 text-red-400' :
                                'bg-zinc-500/20 text-white'
                              }>
                                {product.status === 'published' ? '🟢 Publié' :
                                 product.status === 'draft' ? '🟡 Brouillon' :
                                 product.status === 'archived' ? '🔴 Archivé' :
                                 product.status}
                              </Badge>
                              {product.needs_enrichment && (
                                <AlertTriangle className="w-4 h-4 text-amber-400 inline ml-2" />
                              )}
                            </td>
                            <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                              <Button size="sm" variant="ghost" onClick={() => openProductPanel(product._id)}>
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
                
                {/* Pagination Controls */}
                <div className={cn(
                  "flex items-center justify-between p-4 rounded-lg",
                  isLight ? "bg-white border border-gray-200" : "bg-zinc-900/60 border border-zinc-800"
                )}>
                  {/* Sélecteur de taille de page */}
                  <div className="flex items-center gap-3">
                    <span className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>Afficher</span>
                    <Select value={String(pageSize)} onValueChange={(v) => handlePageSizeChange(Number(v))}>
                      <SelectTrigger className={cn(
                        "w-20",
                        isLight ? "bg-white border-gray-300" : "bg-zinc-800 border-zinc-700 text-white"
                      )}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={isLight ? "bg-white" : "bg-zinc-800 border-zinc-700"}>
                        <SelectItem value="50" className={isLight ? "" : "text-white hover:bg-zinc-700"}>50</SelectItem>
                        <SelectItem value="100" className={isLight ? "" : "text-white hover:bg-zinc-700"}>100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className={cn("text-sm", isLight ? "text-gray-600" : "text-gray-400")}>
                      sur {totalProducts} produit(s)
                    </span>
                  </div>
                  
                  {/* Info page actuelle */}
                  <div className={cn("text-sm", isLight ? "text-gray-700" : "text-white")}>
                    Page {currentPage} sur {totalPages || 1}
                    <span className={cn("ml-2", isLight ? "text-gray-500" : "text-gray-400")}>
                      ({(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalProducts)})
                    </span>
                  </div>
                  
                  {/* Boutons de navigation */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className={isLight ? "" : "border-zinc-700 hover:bg-zinc-800"}
                      data-testid="first-page-btn"
                    >
                      «
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={isLight ? "" : "border-zinc-700 hover:bg-zinc-800"}
                      data-testid="prev-page-btn"
                    >
                      ‹ Précédent
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      className={isLight ? "" : "border-zinc-700 hover:bg-zinc-800"}
                      data-testid="next-page-btn"
                    >
                      Suivant ›
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage >= totalPages}
                      className={isLight ? "" : "border-zinc-700 hover:bg-zinc-800"}
                      data-testid="last-page-btn"
                    >
                      »
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Tab: Margins */}
              <TabsContent value="margins" className="space-y-4">
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Règles de marge par catégorie</CardTitle>
                    <CardDescription>
                      Définissez le multiplicateur de prix pour chaque catégorie
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MarginRulesSection />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Side panel - Product details */}
          {isPanelOpen && selectedProduct && (
            <div className="fixed right-0 top-0 h-full w-[1100px] bg-zinc-900 border-l border-zinc-800 shadow-xl z-[60] overflow-y-auto" data-testid="product-side-panel">
              <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between z-10">
                <h3 className="text-lg font-semibold text-white">Fiche Produit</h3>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/admin/products/${selectedProduct._id || selectedProduct.id}`)}
                    className="border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10"
                    data-testid="edit-product-full-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Édition complète
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setIsPanelOpen(false)} data-testid="close-panel-btn">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Images */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white text-sm">
                      Images ({
                        Array.isArray(selectedProduct.images) 
                          ? selectedProduct.images.length 
                          : ((selectedProduct.images?.main?.length || 0) + (selectedProduct.images?.qc?.length || 0))
                      })
                    </Label>
                    <div className="flex gap-2">
                      {/* Bouton Recapturer les photos */}
                      {selectedProduct.source_url && !selectedProduct.source_url.includes('search?keyword=') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                          onClick={async () => {
                            try {
                              toast.info('Capture en cours...');
                              const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct._id || selectedProduct.id}/capture-photos`, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                                  'Content-Type': 'application/json'
                                }
                              });
                              const data = await res.json();
                              if (data.success) {
                                toast.success(`${data.photos_captured} photo(s) capturée(s) !`);
                                const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct._id || selectedProduct.id}`, {
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
                                });
                                if (prodRes.ok) setSelectedProduct(await prodRes.json());
                              } else {
                                toast.error(data.message || 'Échec de la capture');
                              }
                            } catch (err) {
                              toast.error('Erreur lors de la capture');
                            }
                          }}
                        >
                          <Camera className="w-3 h-3 mr-1" />
                          Recapturer
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Photos Principales */}
                  {(() => {
                    const mainImages = Array.isArray(selectedProduct.images) 
                      ? selectedProduct.images 
                      : (selectedProduct.images?.main || []);
                    const qcImages = selectedProduct.images?.qc || [];
                    const hasQc = selectedProduct.images?.has_qc || qcImages.length > 0;
                    
                    return (
                      <>
                        {mainImages.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs text-gray-400 mb-2">📸 Photos principales ({mainImages.length})</p>
                            <div className="grid grid-cols-4 gap-2">
                              {mainImages.map((img, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={img.startsWith('http') ? img : `${API_URL}${img}`} alt="" className="w-full aspect-square object-cover rounded" />
                                  <button
                                    onClick={async () => {
                                      if (!confirm('Supprimer cette image ?')) return;
                                      try {
                                        const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}/images`, {
                                          method: 'DELETE',
                                          headers: {
                                            'Authorization': `Bearer ${token}`,
                                            'Content-Type': 'application/json'
                                          },
                                          body: JSON.stringify({ image_url: img, image_type: 'main' })
                                        });
                                        if (res.ok) {
                                          toast.success('Image supprimée');
                                          const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          if (prodRes.ok) setSelectedProduct(await prodRes.json());
                                        }
                                      } catch (e) {
                                        toast.error('Erreur');
                                      }
                                    }}
                                    className="absolute top-1 right-1 p-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Supprimer cette image"
                                  >
                                    <X className="w-3 h-3 text-white" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Photos QC */}
                        {hasQc && qcImages.length > 0 && (
                          <div className="mb-4 border-t border-zinc-700 pt-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="bg-green-600 text-white text-[10px] px-2 py-0.5 rounded">✅ QC</span>
                              <p className="text-xs text-gray-400">Photos Qualité ({qcImages.length})</p>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {qcImages.map((img, idx) => (
                                <div key={`qc-${idx}`} className="relative group">
                                  <img src={img.startsWith('http') ? img : `${API_URL}${img}`} alt="" className="w-full aspect-square object-cover rounded border border-green-600/30" />
                                  <span className="absolute top-1 left-1 bg-green-600 text-white text-[8px] px-1 rounded">QC</span>
                                  <button
                                    onClick={async () => {
                                      if (!confirm('Supprimer cette photo QC ?')) return;
                                      try {
                                        const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}/images`, {
                                          method: 'DELETE',
                                          headers: {
                                            'Authorization': `Bearer ${token}`,
                                            'Content-Type': 'application/json'
                                          },
                                          body: JSON.stringify({ image_url: img, image_type: 'qc' })
                                        });
                                        if (res.ok) {
                                          toast.success('Photo QC supprimée');
                                          const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                            headers: { 'Authorization': `Bearer ${token}` }
                                          });
                                          if (prodRes.ok) setSelectedProduct(await prodRes.json());
                                        }
                                      } catch (e) {
                                        toast.error('Erreur');
                                      }
                                    }}
                                    className="absolute top-1 right-1 p-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Supprimer cette photo"
                                  >
                                    <X className="w-3 h-3 text-white" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Badge Photos manquantes */}
                        {mainImages.length === 0 && (
                          <div className="text-center py-4">
                            {selectedProduct.import_metadata?.missing_photos ? (
                              <div className="space-y-2">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-400 text-sm">
                                  <AlertTriangle className="w-4 h-4" />
                                  <span>⚠️ Photos manquantes</span>
                                </div>
                                <p className="text-gray-500 text-xs">
                                  {selectedProduct.import_metadata?.missing_reason === 'search_url' 
                                    ? "URL de recherche — capture manuelle requise"
                                    : "Capture automatique échouée"}
                                </p>
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm">Aucune image</p>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                
                {/* Name - Editable */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-white text-sm">Nom (FR)</Label>
                    <Input 
                      value={selectedProduct.name?.fr || ''} 
                      onChange={(e) => setSelectedProduct({...selectedProduct, name: {...selectedProduct.name, fr: e.target.value}})}
                      className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-white text-xs">Nom (EN)</Label>
                      <Input 
                        value={selectedProduct.name?.en || ''} 
                        onChange={(e) => setSelectedProduct({...selectedProduct, name: {...selectedProduct.name, en: e.target.value}})}
                        className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-white text-xs">Nom (HE)</Label>
                      <Input 
                        value={selectedProduct.name?.he || ''} 
                        onChange={(e) => setSelectedProduct({...selectedProduct, name: {...selectedProduct.name, he: e.target.value}})}
                        className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                        dir="rtl"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Description - Editable */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-white text-sm">Description (FR)</Label>
                    <Textarea 
                      value={selectedProduct.description?.fr || ''} 
                      onChange={(e) => setSelectedProduct({...selectedProduct, description: {...selectedProduct.description, fr: e.target.value}})}
                      className="mt-1 bg-zinc-800 border-zinc-700 text-white min-h-[120px]"
                      placeholder="Description du produit..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-white text-xs">Description (EN)</Label>
                      <Textarea 
                        value={selectedProduct.description?.en || ''} 
                        onChange={(e) => setSelectedProduct({...selectedProduct, description: {...selectedProduct.description, en: e.target.value}})}
                        className="mt-1 bg-zinc-800 border-zinc-700 text-white min-h-[80px] text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-white text-xs">Description (HE)</Label>
                      <Textarea 
                        value={selectedProduct.description?.he || ''} 
                        onChange={(e) => setSelectedProduct({...selectedProduct, description: {...selectedProduct.description, he: e.target.value}})}
                        className="mt-1 bg-zinc-800 border-zinc-700 text-white min-h-[80px] text-sm"
                        dir="rtl"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Tags */}
                <div>
                  <Label className="text-white text-sm">Tags SEO</Label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedProduct.tags?.map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-zinc-700">{tag}</Badge>
                    ))}
                    {(!selectedProduct.tags || selectedProduct.tags.length === 0) && (
                      <span className="text-gray-500 text-sm">Aucun tag</span>
                    )}
                  </div>
                </div>
                
                {/* Catégories */}
                <div className="border-t border-zinc-700 pt-4">
                  <Label className="text-white text-sm flex items-center gap-2">
                    🏷️ Catégories
                    <Badge variant="outline" className="text-xs">
                      {selectedProduct.categories?.length || 0} catégorie(s)
                    </Badge>
                  </Label>
                  <div className="mt-2 space-y-2">
                    {/* Catégories actuelles */}
                    {selectedProduct.categories && selectedProduct.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {selectedProduct.categories.map((catId, idx) => {
                          const cat = categories.find(c => c.id === catId || c._id === catId);
                          return (
                            <Badge 
                              key={idx} 
                              className="bg-indigo-600/30 text-indigo-300 border border-indigo-500/30 flex items-center gap-1"
                            >
                              {cat?.name?.fr || cat?.slug || catId}
                              <button
                                onClick={() => {
                                  setSelectedProduct({
                                    ...selectedProduct,
                                    categories: selectedProduct.categories.filter(id => id !== catId)
                                  });
                                }}
                                className="ml-1 hover:text-red-400"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Sélecteur de catégorie */}
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (value && !selectedProduct.categories?.includes(value)) {
                          setSelectedProduct({
                            ...selectedProduct,
                            categories: [...(selectedProduct.categories || []), value]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                        <SelectValue placeholder="Ajouter une catégorie..." />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                        <div className="px-2 py-1 text-xs text-gray-400 font-semibold">📦 Types de produit</div>
                        {categories.filter(c => c.type !== 'brand').map(cat => (
                          <SelectItem 
                            key={cat.id || cat._id} 
                            value={cat.id || cat._id}
                            className="text-white hover:bg-zinc-700"
                            disabled={selectedProduct.categories?.includes(cat.id || cat._id)}
                          >
                            {cat.emoji || '📁'} {cat.name?.fr || cat.slug}
                          </SelectItem>
                        ))}
                        <div className="px-2 py-1 text-xs text-gray-400 font-semibold border-t border-zinc-700 mt-1 pt-2">🏷️ Marques</div>
                        {categories.filter(c => c.type === 'brand').slice(0, 20).map(cat => (
                          <SelectItem 
                            key={cat.id || cat._id} 
                            value={cat.id || cat._id}
                            className="text-white hover:bg-zinc-700"
                            disabled={selectedProduct.categories?.includes(cat.id || cat._id)}
                          >
                            🏷️ {cat.name?.fr || cat.slug}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Marque détectée */}
                    {selectedProduct.brand && (
                      <p className="text-xs text-gray-500 mt-1">
                        Marque détectée: <span className="text-indigo-400">{selectedProduct.brand}</span>
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Section Variantes / Tailles */}
                <div className="border-t border-zinc-700 pt-4">
                  <Label className="text-white text-sm flex items-center gap-2">
                    📏 Variantes / Tailles 
                    <Badge variant="outline" className="text-xs">
                      {selectedProduct.variants?.length || 0} variante(s)
                    </Badge>
                  </Label>
                  <div className="mt-3">
                    {selectedProduct.variants && selectedProduct.variants.length > 0 ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {selectedProduct.variants.map((variant, idx) => (
                            <div 
                              key={idx} 
                              className={`px-3 py-2 rounded-lg border text-sm ${
                                variant.available 
                                  ? 'border-zinc-600 bg-zinc-800 text-white' 
                                  : 'border-zinc-700 bg-zinc-900 text-gray-500 line-through'
                              }`}
                            >
                              <span className="font-medium">{variant.attributes?.size || variant.sku}</span>
                              {variant.stock_quantity !== undefined && (
                                <span className="text-xs text-gray-400 ml-2">
                                  (stock: {variant.stock_quantity})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Stock total: {selectedProduct.variants.reduce((acc, v) => acc + (v.stock_quantity || 0), 0)} unités
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucune variante définie</p>
                    )}
                  </div>
                </div>
                
                {/* Section Images Variantes (Couleurs) */}
                {selectedProduct.images?.variants && selectedProduct.images.variants.length > 0 && (
                  <div className="border-t border-zinc-700 pt-4">
                    <Label className="text-white text-sm flex items-center gap-2">
                      🎨 Images Variantes (Couleurs)
                      <Badge variant="outline" className="text-xs bg-orange-500/20 text-orange-400 border-orange-500/30">
                        {selectedProduct.images.variants.length} couleur(s)
                      </Badge>
                    </Label>
                    <div className="mt-3 grid grid-cols-6 gap-2">
                      {selectedProduct.images.variants.map((variant, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={variant.path?.startsWith('http') ? variant.path : `${API_URL}${variant.path}`}
                            alt={variant.label || `Variante ${idx + 1}`}
                            className="w-full aspect-square object-cover rounded border-2 border-orange-500/30"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-orange-600/90 text-white text-[10px] text-center py-0.5 rounded-b truncate px-1">
                            {variant.label || `C${idx + 1}`}
                          </div>
                          <button
                            onClick={async () => {
                              if (!confirm(`Supprimer la variante "${variant.label}" ?`)) return;
                              try {
                                const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}/images`, {
                                  method: 'DELETE',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({ image_url: variant.path, image_type: 'variants' })
                                });
                                if (res.ok) {
                                  toast.success('Variante supprimée');
                                  const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                    headers: { 'Authorization': `Bearer ${token}` }
                                  });
                                  if (prodRes.ok) setSelectedProduct(await prodRes.json());
                                }
                              } catch (e) {
                                toast.error('Erreur');
                              }
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Supprimer cette variante"
                          >
                            <X className="w-2 h-2 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Ces images apparaissent dans la galerie du produit et permettent au client de voir les différentes couleurs disponibles.
                    </p>
                  </div>
                )}
                
                {/* Save Button */}
                <Button 
                  className="w-full bg-primary"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                        method: 'PATCH',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          name: selectedProduct.name,
                          description: selectedProduct.description,
                          tags: selectedProduct.tags,
                          categories: selectedProduct.categories
                        })
                      });
                      if (res.ok) {
                        toast.success('Produit mis à jour');
                        fetchProducts();
                      } else {
                        toast.error('Erreur de sauvegarde');
                      }
                    } catch (e) {
                      toast.error('Erreur de connexion');
                    }
                  }}
                >
                  Sauvegarder les modifications
                </Button>
                
                {/* Prices */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm">Prix achat</Label>
                    <div className="space-y-1 mt-1">
                      <p className="text-blue-400 font-bold text-lg">
                        ${selectedProduct.source_price?.price_usd?.toFixed(2) || '—'}
                      </p>
                      <p className="text-gray-400 text-sm">
                        €{selectedProduct.source_price?.price_usd ? (selectedProduct.source_price.price_usd * 0.92).toFixed(2) : '—'}
                      </p>
                      <p className="text-gray-400 text-sm">
                        ₪{selectedProduct.source_price?.price_usd ? (selectedProduct.source_price.price_usd * 3.65).toFixed(2) : '—'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-white text-sm">Prix vente</Label>
                    <div className="space-y-1 mt-1">
                      {/* USD */}
                      <p className="text-white font-semibold text-lg">
                        ${typeof selectedProduct.pricing?.prices?.USD === 'object'
                          ? selectedProduct.pricing.prices.USD.amount?.toFixed(2)
                          : selectedProduct.pricing?.prices?.USD?.toFixed(2) || '—'}
                      </p>
                      {/* EUR */}
                      <p className="text-gray-400 text-sm">
                        €{typeof selectedProduct.pricing?.prices?.EUR === 'object'
                          ? selectedProduct.pricing.prices.EUR.amount?.toFixed(2)
                          : selectedProduct.pricing?.prices?.EUR?.toFixed(2) || '—'}
                      </p>
                      {/* ILS */}
                      <p className="text-gray-400 text-sm">
                        ₪{typeof selectedProduct.pricing?.prices?.ILS === 'object' 
                          ? selectedProduct.pricing.prices.ILS.amount?.toFixed(2)
                          : selectedProduct.pricing?.prices?.ILS?.toFixed(2) || selectedProduct.price?.amount?.toFixed(2) || '—'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label className="text-white text-sm">Marge</Label>
                  <p className={`text-lg font-bold ${selectedProduct.margin_percent > 100 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {selectedProduct.margin_percent?.toFixed(1)}%
                  </p>
                </div>
                
                {/* Variants */}
                <div>
                  <Label className="text-white text-sm">Variantes ({selectedProduct.variants?.length || 0})</Label>
                  <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                    {selectedProduct.variants?.slice(0, 10).map((v, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded text-sm">
                        <span className="text-white">
                          {v.attributes?.color} / {v.attributes?.size}
                        </span>
                        <span className="text-white">Stock: {v.stock_quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Source */}
                <div>
                  <Label className="text-white text-sm">Source</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant="outline">{selectedProduct.source_type}</Badge>
                    {selectedProduct.source_url && (
                      <a 
                        href={selectedProduct.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm"
                      >
                        <ExternalLink className="w-4 h-4 inline" />
                      </a>
                    )}
                  </div>
                </div>
                
                {/* Bouton Réimporter les données */}
                {selectedProduct.source_url && (
                  <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-orange-300">Réimporter les données</p>
                        <p className="text-xs text-gray-400">Rescraper le titre, prix et images depuis la source</p>
                      </div>
                      <Button 
                        size="sm"
                        variant="outline"
                        className="border-orange-500/50 text-orange-300 hover:bg-orange-500/20"
                        onClick={async () => {
                          if (!confirm('Réimporter les données depuis la source ? Les images actuelles seront remplacées.')) return;
                          
                          const toastId = toast.loading('Scraping en cours...');
                          try {
                            const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}/rescrape`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              }
                            });
                            const data = await res.json();
                            
                            if (res.ok && data.success) {
                              toast.success(`${data.images_imported || 0} images importées!`, { id: toastId });
                              // Refresh product
                              const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (prodRes.ok) {
                                setSelectedProduct(await prodRes.json());
                                fetchProducts();
                              }
                            } else {
                              toast.error(data.error || 'Erreur de scraping', { id: toastId });
                            }
                          } catch (e) {
                            toast.error('Erreur de connexion', { id: toastId });
                          }
                        }}
                        data-testid="rescrape-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Réimporter
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Atmosphères & Génération IA */}
                <div className="p-3 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg space-y-3 border border-purple-500/30">
                  <Label className="text-white text-sm flex items-center gap-2">
                    <Palette className="w-4 h-4 text-purple-400" />
                    Génération d'images IA
                  </Label>
                  
                  {/* Atmosphère actuelle */}
                  {selectedProduct.atmosphere_id && (
                    <div className="text-xs text-purple-300">
                      Atmosphère actuelle: {atmospheres.find(a => a.id === selectedProduct.atmosphere_id)?.name || 'N/A'}
                    </div>
                  )}
                  
                  {/* Images AI existantes */}
                  {selectedProduct.ai_generated_images?.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-300">{selectedProduct.ai_generated_images.length} image(s) générée(s)</p>
                        <button
                          onClick={async () => {
                            if (!confirm('Supprimer TOUTES les images AI de ce produit ?')) return;
                            for (const img of selectedProduct.ai_generated_images) {
                              try {
                                await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}/images`, {
                                  method: 'DELETE',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({ image_url: img.url, image_type: 'ai' })
                                });
                              } catch (e) {}
                            }
                            toast.success('Images AI supprimées');
                            // Refresh product
                            const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (res.ok) setSelectedProduct(await res.json());
                          }}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Tout supprimer
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {selectedProduct.ai_generated_images.map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={`${API_URL}${img.url}`} 
                              alt="" 
                              className="w-full aspect-square object-cover rounded border border-purple-500/30"
                              title={img.atmosphere_name || 'Image IA'}
                            />
                            <button
                              onClick={async () => {
                                if (!confirm('Supprimer cette image ?')) return;
                                try {
                                  const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}/images`, {
                                    method: 'DELETE',
                                    headers: {
                                      'Authorization': `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ image_url: img.url, image_type: 'ai' })
                                  });
                                  if (res.ok) {
                                    toast.success('Image supprimée');
                                    // Refresh product
                                    const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                      headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    if (prodRes.ok) setSelectedProduct(await prodRes.json());
                                  } else {
                                    toast.error('Erreur de suppression');
                                  }
                                } catch (e) {
                                  toast.error('Erreur de connexion');
                                }
                              }}
                              className="absolute top-1 right-1 p-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Supprimer cette image"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] text-white text-center py-0.5 truncate px-1">
                              {img.atmosphere_name || 'AI'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Toggle: Atmosphère vs Prompt Libre */}
                  <div className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded-lg">
                    <button
                      className={cn(
                        "flex-1 px-3 py-2 text-sm rounded transition-colors",
                        !freePromptMode 
                          ? "bg-purple-600 text-white" 
                          : "bg-zinc-700 text-gray-400 hover:bg-zinc-600"
                      )}
                      onClick={() => setFreePromptMode(false)}
                    >
                      Atmosphère
                    </button>
                    <button
                      className={cn(
                        "flex-1 px-3 py-2 text-sm rounded transition-colors",
                        freePromptMode 
                          ? "bg-gradient-to-r from-cyan-600 to-blue-600 text-white" 
                          : "bg-zinc-700 text-gray-400 hover:bg-zinc-600"
                      )}
                      onClick={() => setFreePromptMode(true)}
                    >
                      Prompt Libre
                    </button>
                  </div>
                  
                  {/* Mode Atmosphère */}
                  {!freePromptMode && (
                    <>
                      <Select 
                        value={selectedAtmosphereId} 
                        onValueChange={setSelectedAtmosphereId}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white">
                          <SelectValue placeholder="Choisir une atmosphère..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700 z-[100]">
                          {atmospheres.length === 0 ? (
                            <SelectItem value="none" disabled className="text-gray-400">
                              Aucune atmosphère disponible
                            </SelectItem>
                          ) : (
                            atmospheres.filter(a => a.is_active).map(atm => (
                              <SelectItem 
                                key={atm.id} 
                                value={atm.id}
                                className="text-white hover:bg-zinc-700 cursor-pointer"
                              >
                                {atm.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      
                      <Button
                        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                        disabled={!selectedAtmosphereId || isGeneratingAI}
                        onClick={async () => {
                          if (!selectedAtmosphereId) {
                            toast.error('Sélectionnez une atmosphère');
                            return;
                          }
                          
                          setIsGeneratingAI(true);
                          const productId = selectedProduct.id || selectedProduct._id;
                          
                          try {
                            toast.info('Génération de l\'image en cours... (~20 secondes)', { duration: 5000 });
                            
                            const controller = new AbortController();
                            const timeoutId = setTimeout(() => controller.abort(), 120000);
                            
                            const res = await fetch(`${API_URL}/api/admin/atmospheres/${selectedAtmosphereId}/generate-single`, {
                              method: 'POST',
                              headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({
                                product_id: productId,
                                provider: 'gemini'
                              }),
                              signal: controller.signal
                            });
                            
                            clearTimeout(timeoutId);
                            
                            if (res.ok) {
                              const data = await res.json();
                              toast.success(`✅ Image générée ! (${data.total_ai_images} image(s) au total)`);
                              
                              const prodRes = await fetch(`${API_URL}/api/sourcing/products/${productId}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (prodRes.ok) {
                                const updatedProduct = await prodRes.json();
                                setSelectedProduct(updatedProduct);
                              }
                            } else {
                              const err = await res.json().catch(() => ({}));
                              toast.error(err.detail || 'Erreur lors de la génération');
                            }
                          } catch (err) {
                            if (err.name === 'AbortError') {
                              toast.error('Timeout: la génération a pris trop de temps.');
                            } else {
                              toast.error('Erreur de connexion au serveur');
                            }
                          } finally {
                            setIsGeneratingAI(false);
                          }
                        }}
                        data-testid="generate-ai-image-btn"
                      >
                        {isGeneratingAI ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Image className="w-4 h-4 mr-2" />
                        )}
                        Générer avec atmosphère
                      </Button>
                    </>
                  )}
                  
                  {/* Mode Prompt Libre */}
                  {freePromptMode && (
                    <>
                      {/* Sélecteur de style */}
                      <div className="grid grid-cols-3 gap-1">
                        {[
                          { id: 'editorial', icon: '📸', label: 'Editorial' },
                          { id: 'cinematic', icon: '🎬', label: 'Ciné' },
                          { id: 'minimalist', icon: '⬜', label: 'Minimal' },
                          { id: 'urban', icon: '🏙️', label: 'Urbain' },
                          { id: 'luxury', icon: '✨', label: 'Luxe' },
                          { id: 'dramatic', icon: '🎭', label: 'Drama' },
                        ].map(style => (
                          <button
                            key={style.id}
                            className={cn(
                              "p-2 text-xs rounded border transition-colors",
                              freePromptStyle === style.id
                                ? "border-cyan-500 bg-cyan-500/20 text-white"
                                : "border-zinc-700 hover:border-zinc-500 text-gray-400"
                            )}
                            onClick={() => setFreePromptStyle(style.id)}
                          >
                            <span className="block text-lg mb-1">{style.icon}</span>
                            {style.label}
                          </button>
                        ))}
                      </div>
                      
                      {/* Textarea pour le prompt avec micro */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Décrivez l'ambiance souhaitée</span>
                          <VoiceInput 
                            onTranscript={(text) => setFreePrompt(prev => prev + ' ' + text)}
                            className="border-zinc-700"
                          />
                        </div>
                        <Textarea
                          placeholder="Ex: Sur un podium de fashion week, lumière dramatique... ou utilisez le micro 🎤"
                          value={freePrompt}
                          onChange={(e) => setFreePrompt(e.target.value)}
                          className="bg-zinc-800 border-zinc-600 text-white placeholder:text-gray-500 min-h-[80px]"
                        />
                      </div>
                      
                      <Button
                        className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                        disabled={isGeneratingAI}
                        onClick={async () => {
                          setIsGeneratingAI(true);
                          const productId = selectedProduct.id || selectedProduct._id;
                          
                          try {
                            toast.info('Génération avec prompt libre... (~20 secondes)', { duration: 5000 });
                            
                            const controller = new AbortController();
                            const timeoutId = setTimeout(() => controller.abort(), 120000);
                            
                            const res = await fetch(`${API_URL}/api/admin/ai/generate-product-images`, {
                              method: 'POST',
                              headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({
                                product_ids: [productId],
                                visual_style: freePromptStyle,
                                custom_prompt: freePrompt || null,
                                aspect_ratio: '1:1',
                                variants_count: 1,
                                set_as_main: true
                              }),
                              signal: controller.signal
                            });
                            
                            clearTimeout(timeoutId);
                            
                            if (res.ok) {
                              const data = await res.json();
                              toast.success(`✅ Image générée avec succès !`);
                              setFreePrompt('');
                              
                              const prodRes = await fetch(`${API_URL}/api/sourcing/products/${productId}`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (prodRes.ok) {
                                const updatedProduct = await prodRes.json();
                                setSelectedProduct(updatedProduct);
                              }
                              fetchProducts();
                            } else {
                              const err = await res.json().catch(() => ({}));
                              toast.error(err.detail || 'Erreur lors de la génération');
                            }
                          } catch (err) {
                            if (err.name === 'AbortError') {
                              toast.error('Timeout: la génération a pris trop de temps.');
                            } else {
                              toast.error('Erreur de connexion au serveur');
                            }
                          } finally {
                            setIsGeneratingAI(false);
                          }
                        }}
                        data-testid="generate-free-prompt-btn"
                      >
                        {isGeneratingAI ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Wand2 className="w-4 h-4 mr-2" />
                        )}
                        Générer avec prompt libre
                      </Button>
                    </>
                  )}
                  
                  <p className="text-xs text-gray-400">
                    Génération via Gemini Nano Banana
                  </p>
                </div>
                
                {/* Actions */}
                <div className="pt-4 border-t border-zinc-700 space-y-3">
                  {/* Status & Publication Section */}
                  <div className="p-3 bg-zinc-800/50 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-white text-sm">Statut actuel</Label>
                      <Badge className={
                        selectedProduct.status === 'published' ? 'bg-green-500/20 text-green-400 border-green-500/50' :
                        selectedProduct.status === 'archived' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
                        'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                      }>
                        {selectedProduct.status === 'published' ? '🟢 Publié' :
                         selectedProduct.status === 'archived' ? '🔴 Archivé' :
                         '🟡 Brouillon'}
                      </Badge>
                    </div>
                    
                    {/* Boutons de publication */}
                    <div className="flex gap-2">
                      {selectedProduct.status !== 'published' && (
                        <Button 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}/publish`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (res.ok) {
                                toast.success('Produit publié avec succès!');
                                // Rafraîchir le produit
                                const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (prodRes.ok) {
                                  setSelectedProduct(await prodRes.json());
                                }
                                fetchProducts();
                              } else {
                                toast.error('Erreur lors de la publication');
                              }
                            } catch (e) {
                              toast.error('Erreur de connexion');
                            }
                          }}
                          data-testid="publish-product-btn"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Publier
                        </Button>
                      )}
                      
                      {selectedProduct.status === 'published' && (
                        <Button 
                          className="flex-1"
                          variant="outline"
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}/unpublish`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (res.ok) {
                                toast.success('Produit dépublié (retour en brouillon)');
                                // Rafraîchir le produit
                                const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (prodRes.ok) {
                                  setSelectedProduct(await prodRes.json());
                                }
                                fetchProducts();
                              } else {
                                toast.error('Erreur lors de la dépublication');
                              }
                            } catch (e) {
                              toast.error('Erreur de connexion');
                            }
                          }}
                          data-testid="unpublish-product-btn"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Dépublier
                        </Button>
                      )}
                      
                      {selectedProduct.status !== 'archived' && (
                        <Button 
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          onClick={async () => {
                            if (!confirm('Archiver ce produit ? Il ne sera plus visible sur le site.')) return;
                            try {
                              const res = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                method: 'PATCH',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ status: 'archived' })
                              });
                              if (res.ok) {
                                toast.success('Produit archivé');
                                const prodRes = await fetch(`${API_URL}/api/sourcing/products/${selectedProduct.id || selectedProduct._id}`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (prodRes.ok) {
                                  setSelectedProduct(await prodRes.json());
                                }
                                fetchProducts();
                              }
                            } catch (e) {
                              toast.error('Erreur de connexion');
                            }
                          }}
                          data-testid="archive-product-btn"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => window.open(`/product/${selectedProduct.slug}`, '_blank')}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Voir sur storefront
                  </Button>
                  
                  {/* Import Reviews Section */}
                  <div className="p-3 bg-zinc-800/50 rounded-lg space-y-2">
                    <Label className="text-white text-sm flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Importer des avis
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={reviewsImportCount}
                        onChange={(e) => setReviewsImportCount(parseInt(e.target.value) || 15)}
                        className="w-20 bg-zinc-700 border-zinc-600"
                        placeholder="15"
                      />
                      <Button
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        disabled={isImportingReviews}
                        onClick={async () => {
                          setIsImportingReviews(true);
                          try {
                            const res = await fetch(`${API_URL}/api/products/${selectedProduct.id || selectedProduct._id}/reviews/import?count=${reviewsImportCount}`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              toast.success(`${data.imported_count} avis importés en file d'attente`);
                            } else {
                              toast.error('Erreur lors de l\'import');
                            }
                          } catch (err) {
                            toast.error('Erreur de connexion');
                          } finally {
                            setIsImportingReviews(false);
                          }
                        }}
                        data-testid="import-reviews-btn"
                      >
                        {isImportingReviews ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Star className="w-4 h-4 mr-2" />
                        )}
                        Importer
                      </Button>
                    </div>
                    <p className="text-xs text-gray-200">
                      Les avis seront ajoutés en file d'attente de modération
                    </p>
                  </div>
                  
                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => navigate('/admin/reviews/queue')}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Voir la file de modération
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      </div>
      
      {/* Modal Multi-URL Import */}
      {showMultiUrlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setShowMultiUrlModal(false);
              setMultiUrlText('');
              setDetectedUrls([]);
            }}
          />
          
          {/* Modal */}
          <div className={cn(
            "relative w-full max-w-2xl mx-4 rounded-xl shadow-2xl border",
            isLight ? "bg-white border-gray-200" : "bg-zinc-900 border-zinc-700"
          )}>
            {/* Header */}
            <div className={cn(
              "flex items-center justify-between p-4 border-b",
              isLight ? "border-gray-200" : "border-zinc-700"
            )}>
              <div>
                <h3 className={cn(
                  "text-lg font-semibold flex items-center gap-2",
                  isLight ? "text-gray-900" : "text-white"
                )}>
                  <FileText className="w-5 h-5 text-amber-500" />
                  Import Multi-URL
                </h3>
                <p className={cn("text-sm mt-0.5", isLight ? "text-gray-500" : "text-gray-400")}>
                  Collez plusieurs URLs d'un coup - détection automatique
                </p>
              </div>
              <button
                onClick={() => {
                  setShowMultiUrlModal(false);
                  setMultiUrlText('');
                  setDetectedUrls([]);
                }}
                className={cn(
                  "p-1.5 rounded-lg transition-colors",
                  isLight ? "hover:bg-gray-100" : "hover:bg-zinc-800"
                )}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Textarea */}
              <div>
                <Label className={cn("text-sm mb-2 block", isLight ? "text-gray-700" : "text-gray-300")}>
                  Collez vos URLs ici (une par ligne ou séparées par des espaces)
                </Label>
                <Textarea
                  placeholder={`https://www.acbuy.com/product?id=123456
https://www.acbuy.com/product?id=789012
https://www.acbuy.com/product?id=345678
...`}
                  value={multiUrlText}
                  onChange={(e) => handleMultiUrlPaste(e.target.value)}
                  onPaste={(e) => {
                    // Détection immédiate au collage
                    setTimeout(() => handleMultiUrlPaste(e.target.value), 0);
                  }}
                  className={cn(
                    "min-h-[200px] font-mono text-sm",
                    isLight ? "bg-gray-50 border-gray-300" : "bg-zinc-800 border-zinc-600"
                  )}
                  data-testid="multi-url-textarea"
                />
              </div>
              
              {/* URLs détectées */}
              {detectedUrls.length > 0 && (
                <div className={cn(
                  "rounded-lg p-3 border",
                  isLight ? "bg-green-50 border-green-200" : "bg-green-900/20 border-green-700/50"
                )}>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className={cn(
                      "font-medium",
                      isLight ? "text-green-700" : "text-green-400"
                    )}>
                      {detectedUrls.length} URL(s) valide(s) détectée(s)
                    </span>
                  </div>
                  <div className={cn(
                    "max-h-32 overflow-y-auto space-y-1 text-xs font-mono",
                    isLight ? "text-green-600" : "text-green-300"
                  )}>
                    {detectedUrls.slice(0, 10).map((u, i) => (
                      <div key={u.id} className="truncate">
                        {i + 1}. {u.url}
                      </div>
                    ))}
                    {detectedUrls.length > 10 && (
                      <div className="text-gray-400">
                        ... et {detectedUrls.length - 10} autres
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Message si pas d'URLs */}
              {multiUrlText && detectedUrls.length === 0 && (
                <div className={cn(
                  "rounded-lg p-3 border",
                  isLight ? "bg-amber-50 border-amber-200" : "bg-amber-900/20 border-amber-700/50"
                )}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <span className={cn(
                      "text-sm",
                      isLight ? "text-amber-700" : "text-amber-400"
                    )}>
                      Aucune URL valide détectée. Vérifiez que vos URLs contiennent "acbuy.com", "1688.com" ou "taobao.com"
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className={cn(
              "flex items-center justify-end gap-3 p-4 border-t",
              isLight ? "border-gray-200 bg-gray-50" : "border-zinc-700 bg-zinc-800/50"
            )}>
              <Button
                variant="outline"
                onClick={() => {
                  setShowMultiUrlModal(false);
                  setMultiUrlText('');
                  setDetectedUrls([]);
                }}
                className={isLight ? "border-gray-300" : "border-zinc-600"}
              >
                Annuler
              </Button>
              <Button
                onClick={confirmMultiUrlImport}
                disabled={detectedUrls.length === 0}
                className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter {detectedUrls.length} URL(s) à la liste
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Dialogue de génération d'images IA */}
      <AIProductImageGenerator
        open={showAiImageGenerator}
        onOpenChange={setShowAiImageGenerator}
        selectedProducts={products.filter(p => selectedProducts.includes(p._id))}
        token={token}
        onComplete={() => {
          // Rafraîchir la liste des produits
          fetchProducts();
          setSelectedProducts([]);
        }}
      />

      {/* Dialog: Assignation de catégories en masse */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className={cn(
          "max-w-md",
          isLight ? "bg-white" : "bg-zinc-900 border-zinc-800"
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isLight ? "text-gray-900" : "text-white")}>
              <FolderOpen className="w-5 h-5 text-green-500" />
              Catégoriser {selectedProducts.length} produit(s)
            </DialogTitle>
            <DialogDescription className={isLight ? "text-gray-600" : "text-gray-400"}>
              Sélectionnez les catégories à assigner aux produits sélectionnés
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3 max-h-[300px] overflow-y-auto">
            {categories.filter(c => c.is_active).map((category) => {
              const isSelected = selectedCategoryIds.includes(category.id);
              return (
                <div
                  key={category.id}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedCategoryIds(prev => prev.filter(id => id !== category.id));
                    } else {
                      setSelectedCategoryIds(prev => [...prev, category.id]);
                    }
                  }}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                    isSelected
                      ? isLight
                        ? "border-green-500 bg-green-50"
                        : "border-green-500 bg-green-500/10"
                      : isLight
                        ? "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        : "border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50"
                  )}
                >
                  <Checkbox checked={isSelected} className="pointer-events-none" />
                  <span className="text-xl">{category.emoji || '🏷️'}</span>
                  <div className="flex-1">
                    <p className={cn("font-medium", isLight ? "text-gray-900" : "text-white")}>
                      {category.name?.fr || category.slug}
                    </p>
                    <p className={cn("text-xs", isLight ? "text-gray-500" : "text-gray-400")}>
                      {category.products_count || 0} produits
                    </p>
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                </div>
              );
            })}

            {categories.filter(c => c.is_active).length === 0 && (
              <div className={cn(
                "text-center py-8",
                isLight ? "text-gray-500" : "text-gray-400"
              )}>
                Aucune catégorie disponible
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowCategoryDialog(false);
                setSelectedCategoryIds([]);
              }}
              className={isLight ? "border-gray-300" : "border-zinc-700"}
            >
              Annuler
            </Button>
            <Button
              onClick={handleBulkAssignCategories}
              disabled={isAssigningCategories || selectedCategoryIds.length === 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {isAssigningCategories ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Assigner ({selectedCategoryIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Appliquer Marge */}
      <Dialog open={showMarginDialog} onOpenChange={setShowMarginDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white">Appliquer une marge</DialogTitle>
            <DialogDescription>
              Recalculer le prix de vente pour {selectedProducts.length} produit(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm text-gray-300">Multiplicateur de marge</label>
              <div className="grid grid-cols-4 gap-2">
                {[2, 2.5, 3, 4].map(m => (
                  <Button
                    key={m}
                    variant={selectedMargin === m ? 'default' : 'outline'}
                    className={selectedMargin === m ? 'bg-yellow-600' : ''}
                    onClick={() => setSelectedMargin(m)}
                  >
                    x{m}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                x{selectedMargin} = {Math.round((selectedMargin - 1) * 100)}% de marge
              </p>
            </div>
            <div className="p-3 bg-zinc-800 rounded-lg text-sm">
              <p className="text-gray-300">
                Exemple: Prix source 100 CNY → Prix vente <span className="text-yellow-400 font-bold">{(100 * selectedMargin).toFixed(0)} ₪</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarginDialog(false)}>
              Annuler
            </Button>
            <Button 
              className="bg-yellow-600 hover:bg-yellow-700"
              onClick={handleBulkApplyMargin}
              disabled={isApplyingMargin}
            >
              {isApplyingMargin ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Percent className="w-4 h-4 mr-2" />}
              Appliquer x{selectedMargin}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminSourcingPage;
